// src/providers/AuthProvider.tsx
import { createContext, useState, useCallback, useMemo, useEffect } from "react";
import { jsx } from "react/jsx-runtime";
var AuthContext = createContext(null);
var defaultSocialSignIn = async () => {
  console.warn("Social sign-in not configured");
};
function AuthProvider({ children, initialState, socialSignIn = defaultSocialSignIn, sessionBridge }) {
  const [user, setUser] = useState(initialState?.user ?? null);
  const [token, setToken] = useState(
    initialState?.token ?? null
  );
  const [refreshToken, setRefreshToken] = useState(
    initialState?.refreshToken ?? null
  );
  const [legacyToken, setLegacyToken] = useState(
    initialState?.legacyToken ?? null
  );
  const [isLoading, setIsLoading] = useState(
    initialState?.isLoading ?? false
  );
  const refreshSession = useCallback(async () => {
    if (!sessionBridge) return;
    setIsLoading(true);
    try {
      const data = await sessionBridge.get();
      if (data?.user && data?.token) {
        setUser(data.user);
        setToken(data.token);
      } else {
        setUser(null);
        setToken(null);
        setRefreshToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [sessionBridge]);
  useEffect(() => {
    if (initialState?.user && initialState?.token) return;
    if ((!user || !token) && sessionBridge) {
      void refreshSession();
    }
  }, []);
  const setTokens = useCallback(
    (newToken, newRefreshToken, newLegacyToken) => {
      setToken(newToken);
      setRefreshToken(newRefreshToken);
      setLegacyToken(newLegacyToken ?? null);
    },
    []
  );
  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setLegacyToken(null);
  }, []);
  const value = useMemo(
    () => ({
      user,
      token,
      refreshToken,
      isAuthenticated: !!user && !!token,
      legacyToken,
      isLoading,
      setUser,
      setTokens,
      clearAuth,
      socialSignIn,
      refreshSession,
      sessionBridge
    }),
    [user, token, refreshToken, legacyToken, isLoading, setTokens, clearAuth, socialSignIn, refreshSession, sessionBridge]
  );
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}

// src/providers/LocalizationProvider.tsx
import { createContext as createContext2, useContext, useMemo as useMemo2 } from "react";

// src/localization/en.json
var en_default = {
  login_title: "Login",
  signup_title: "Sign Up",
  forgot_password_title: "Forgot Password",
  reset_password_title: "Reset Password",
  email_label: "Email",
  password_label: "Password",
  confirm_password_label: "Confirm Password",
  first_name_label: "First Name",
  last_name_label: "Last Name",
  login_button: "Login",
  signup_button: "Sign Up",
  submit_button: "Submit",
  reset_password_button: "Reset Password",
  forgot_password_link: "Forgot your password?",
  signup_link: "Don't have an account? Sign up",
  login_link: "Already have an account? Login",
  back_to_login: "Back to Login",
  email_required: "Email is required",
  email_invalid: "Invalid email format",
  password_required: "Password is required",
  password_min_length: "Password must be at least {length} characters",
  passwords_must_match: "Passwords must match",
  first_name_required: "First name is required",
  last_name_required: "Last name is required",
  login_success: "Login successful",
  signup_success: "Account created successfully",
  forgot_password_success: "Password reset email sent",
  reset_password_success: "Password reset successfully",
  login_error: "Login failed. Please check your credentials.",
  signup_error: "Sign up failed. Please try again.",
  forgot_password_error: "Failed to send reset email. Please try again.",
  reset_password_error: "Failed to reset password. Please try again.",
  logout: "Logout",
  loading: "Loading...",
  invalidCredentials: "Invalid credentials",
  loginError: "Login failed",
  rememberMe: "Remember me",
  "login.secureYourAdventure": "Secure Your Adventure",
  "login.discoverBali": "Discover the magic of Bali with our exclusive travel packages.",
  "login.exploreHiddenGems": "Explore Hidden Gems",
  "login.uncoverDestinations": "Uncover amazing destinations waiting to be explored.",
  "login.adventureAwaits": "Adventure Awaits",
  "login.fromMountainPeaks": "From mountain peaks to ocean depths, your journey starts here.",
  "login.emailOrPhone": "Email or Phone",
  "login.emailOrPhonePlaceholder": "Enter your email",
  "login.password": "Password",
  "login.passwordPlaceholder": "Enter your password",
  "login.forgotPassword": "Forgot password?",
  "login.signInError": "Sign in failed. Please try again.",
  "login.continue": "Continue",
  "login.signingIn": "Signing in...",
  "login.logIn": "Log In",
  "login.sign_in_with_google": "Sign in with Google",
  "login.sign_in_with_facebook": "Sign in with Facebook",
  "login.sign_in_with_apple": "Sign in with Apple",
  "login.dontHaveAccount": "Don't have an account?",
  "login.signUp": "Sign up",
  "login.whatsYourEmail": "What's your email?",
  "login.accessYourAccount": "Access your account",
  "login.disclaimerPrefix": "By continuing on this page, you accept our",
  "login.termsOfUse": "Terms of Use",
  "login.disclaimerMiddle": "and understand that your information will be used as described in our",
  "login.privacyPolicy": "Privacy Policy",
  "login.or": "or",
  "login.signInWithPasskey": "Sign in with Passkey",
  "login.heroTitle": "Join Ticket\xF3n!",
  "login.heroSubtitle": "The events you love, right around the corner!",
  "signup.joinAdventure": "Join the Adventure",
  "signup.discoverWorld": "Create your account and discover the world with us.",
  "signup.exploreWithUs": "Explore With Us",
  "signup.createYourJourney": "Create your journey and make unforgettable memories.",
  "signup.startYourStory": "Start Your Story",
  "signup.memoriesAwait": "Incredible memories await. Sign up and begin your adventure.",
  "signup.firstName": "First Name",
  "signup.firstNamePlaceholder": "First name",
  "signup.lastName": "Last Name",
  "signup.lastNamePlaceholder": "Last name",
  "signup.email": "Email",
  "signup.emailPlaceholder": "Enter your email",
  "signup.password": "Password",
  "signup.passwordPlaceholder": "Create a password",
  "signup.confirmPassword": "Confirm Password",
  "signup.confirmPasswordPlaceholder": "Confirm your password",
  "signup.acceptTerms": "I accept the",
  "signup.termsOfService": "Terms of Service",
  "signup.creatingAccount": "Creating account...",
  "signup.createAccount": "Create Account",
  "signup.finishCreatingAccount": "Finish registration with {provider}",
  "signup.finishingAccount": "Finishing registration...",
  "signup.alreadyHaveAccount": "Already have an account?",
  "signup.signIn": "Sign in",
  "signup.passwordMismatch": "Passwords do not match",
  "signup.termsRequired": "You must accept the terms",
  "signup.passwordsDoNotMatch": "Passwords do not match",
  "signup.signUpError": "Sign up failed",
  "signup.tryAgainLater": "Something went wrong. Please try again later",
  "signup.heroTitle": "Join Ticket\xF3n!",
  "signup.heroSubtitle": "Create your account and start your adventure!",
  "signup.createYourAccount": "Create your account",
  "signup.completeYourProfile": "Complete your {provider} profile",
  "signup.completeProfileDescription": "Just a few more details to create your account with {provider}",
  "signup.disclaimerPrefix": "By continuing on this page, you accept our",
  "signup.termsOfUse": "Terms of Use",
  "signup.disclaimerMiddle": "and understand that your information will be used as described in our",
  "signup.privacyPolicy": "Privacy Policy",
  "countries.us": "United States",
  "countries.ca": "Canada",
  "countries.mx": "Mexico",
  "countries.br": "Brazil",
  "countries.ar": "Argentina",
  "countries.co": "Colombia",
  "countries.pe": "Peru",
  "countries.ve": "Venezuela",
  "countries.cl": "Chile",
  "countries.ec": "Ecuador",
  "countries.gt": "Guatemala",
  "countries.cu": "Cuba",
  "countries.sv": "El Salvador",
  "countries.gb": "United Kingdom",
  "countries.fr": "France",
  "countries.de": "Germany",
  "countries.it": "Italy",
  "countries.es": "Spain",
  "countries.nl": "Netherlands",
  "countries.se": "Sweden",
  "countries.no": "Norway",
  "countries.ch": "Switzerland",
  "countries.be": "Belgium",
  "countries.pl": "Poland",
  "countries.pt": "Portugal",
  "countries.ie": "Ireland",
  "countries.gr": "Greece",
  "countries.dk": "Denmark",
  "signup.passwordRequired": "Password is required",
  "signup.passwordNeedsNumber": "Password must contain at least one number",
  "signup.passwordNeedsSpecial": "Password must contain at least one special character",
  "signup.confirmPasswordRequired": "Please confirm your password",
  "signup.phoneCountryCode": "Country Code",
  "signup.phone": "Phone",
  "signup.phonePlaceholder": "Phone number",
  "signup.phoneCountryCodePlaceholder": "Select country code",
  "signup.phoneRequired": "Phone number is required",
  "signup.phoneCountryCodeRequired": "Country code is required",
  "signup.invalidPhoneNumeric": "The phone number must contain digits only.",
  "signup.invalidPhoneLength": "The phone number must be exactly {digits} digits long for this country.",
  "signup.recaptchaError": "reCAPTCHA verification failed. Please try again.",
  "signup.firstNameRequired": "First name is required",
  "signup.invalidFirstName": "First name can only contain letters, spaces, hyphens and apostrophes",
  "signup.lastNameRequired": "Last name is required",
  "signup.invalidLastName": "Last name can only contain letters, spaces, hyphens and apostrophes",
  "signup.emailRequired": "Email is required",
  "signup.invalidEmail": "Invalid email format",
  "forgotPassword.forgotPassword": "Forgot Password",
  "forgotPassword.enterEmailToReset": "Enter your email address and we'll send you instructions to reset your password.",
  "forgotPassword.email": "Email",
  "forgotPassword.emailPlaceholder": "Enter your email",
  "forgotPassword.sendingEmail": "Sending...",
  "forgotPassword.sendResetEmail": "Send Reset Email",
  "forgotPassword.sendEmailError": "Failed to send reset email. Please try again.",
  "forgotPassword.emailSent": "Email Sent!",
  "forgotPassword.checkYourEmail": "If an account exists with this email, a reset code has been sent.",
  "forgotPassword.emailInstructions": "If you don't see the email, check your spam folder or try again.",
  "forgotPassword.backToLogin": "Back to Login",
  "forgotPassword.rememberPassword": "Remember your password?",
  "otpVerification.verifyCode": "Verify Code",
  "otpVerification.enterCodeSent": "Enter the 6-digit code we sent to your email.",
  "otpVerification.verificationCode": "Verification Code",
  "otpVerification.verifying": "Verifying...",
  "otpVerification.incompleteCode": "Please enter the complete 6-digit code.",
  "otpVerification.verificationError": "Verification failed. Please try again.",
  "otpVerification.didntReceiveCode": "Didn't receive the code?",
  "otpVerification.sending": "Sending...",
  "otpVerification.resendIn": "Resend in",
  "otpVerification.resendCode": "Resend Code",
  "otpVerification.resendError": "Failed to resend code. Please try again.",
  "otpVerification.wrongEmail": "Wrong email?",
  "otpVerification.backToLogin": "Back to Login",
  "resetPassword.resetPassword": "Reset Password",
  "resetPassword.enterNewPassword": "Enter your new password below.",
  "resetPassword.newPassword": "New Password",
  "resetPassword.newPasswordPlaceholder": "Enter new password",
  "resetPassword.confirmNewPassword": "Confirm New Password",
  "resetPassword.confirmNewPasswordPlaceholder": "Confirm new password",
  "resetPassword.updatingPassword": "Updating...",
  "resetPassword.updatePassword": "Update Password",
  "resetPassword.passwordMismatch": "Passwords do not match",
  "resetPassword.passwordTooShort": "Password must be at least 8 characters",
  "resetPassword.resetError": "Failed to reset password. Please try again.",
  "resetPassword.passwordResetSuccess": "Password Reset!",
  "resetPassword.passwordUpdated": "Your password has been updated successfully.",
  "resetPassword.youCanNowLogin": "You can now log in with your new password.",
  "resetPassword.continueToLogin": "Continue to Login",
  "resetPassword.rememberPassword": "Remember your password?",
  "resetPassword.backToLogin": "Back to Login",
  "resetPassword.passwordsDoNotMatch": "Passwords do not match",
  "resetPassword.email": "Email",
  "passwordStrength.passwordStrength": "Password Strength",
  "passwordStrength.weak": "Weak",
  "passwordStrength.medium": "Medium",
  "passwordStrength.strong": "Strong",
  "passwordStrength.atLeast8Chars": "At least 8 characters",
  "passwordStrength.lowercaseLetter": "Lowercase letter",
  "passwordStrength.uppercaseLetter": "Uppercase letter",
  "passwordStrength.number": "Number",
  "passwordStrength.specialCharacter": "Special character",
  "accountLinking.heroTitle": "Link Your Account",
  "accountLinking.heroSubtitle": "Connect your {provider} account",
  "accountLinking.accountFound": "Account Found",
  "accountLinking.existingAccountMessage": "An account with this email already exists. Would you like to link it with {provider}?",
  "accountLinking.email": "Email",
  "accountLinking.linkingExplanation": "By continuing, your existing account will be linked to {provider}. You'll be able to sign in with {provider} or your email and password.",
  "accountLinking.continueWithProvider": "Continue with {provider}",
  "accountLinking.loginWithEmail": "Login with email instead",
  "accountLinking.linking": "Linking with {provider}...",
  "accountLinking.linkingError": "Failed to link with {provider}. Please try again.",
  "accountLinking.securityNote": "Your account security is our priority. This action will connect your existing account to {provider}."
};

// src/localization/es.json
var es_default = {
  login_title: "Iniciar Sesi\xF3n",
  signup_title: "Registrarse",
  forgot_password_title: "Olvid\xE9 mi Contrase\xF1a",
  reset_password_title: "Restablecer Contrase\xF1a",
  email_label: "Correo Electr\xF3nico",
  password_label: "Contrase\xF1a",
  confirm_password_label: "Confirmar Contrase\xF1a",
  first_name_label: "Nombre",
  last_name_label: "Apellido",
  login_button: "Iniciar Sesi\xF3n",
  signup_button: "Registrarse",
  submit_button: "Enviar",
  reset_password_button: "Restablecer Contrase\xF1a",
  forgot_password_link: "\xBFOlvidaste tu contrase\xF1a?",
  signup_link: "\xBFNo tienes cuenta? Reg\xEDstrate",
  login_link: "\xBFYa tienes cuenta? Inicia sesi\xF3n",
  back_to_login: "Volver al Inicio de Sesi\xF3n",
  email_required: "El correo electr\xF3nico es requerido",
  email_invalid: "Formato de correo electr\xF3nico inv\xE1lido",
  password_required: "La contrase\xF1a es requerida",
  password_min_length: "La contrase\xF1a debe tener al menos {length} caracteres",
  passwords_must_match: "Las contrase\xF1as deben coincidir",
  first_name_required: "El nombre es requerido",
  last_name_required: "El apellido es requerido",
  login_success: "Inicio de sesi\xF3n exitoso",
  signup_success: "Cuenta creada exitosamente",
  forgot_password_success: "Correo de restablecimiento enviado",
  reset_password_success: "Contrase\xF1a restablecida exitosamente",
  login_error: "Error al iniciar sesi\xF3n. Verifica tus credenciales.",
  signup_error: "Error al registrarse. Por favor intenta de nuevo.",
  forgot_password_error: "Error al enviar el correo. Por favor intenta de nuevo.",
  reset_password_error: "Error al restablecer la contrase\xF1a. Por favor intenta de nuevo.",
  logout: "Cerrar Sesi\xF3n",
  loading: "Cargando...",
  invalidCredentials: "Credenciales inv\xE1lidas",
  loginError: "Error al iniciar sesi\xF3n",
  rememberMe: "Recu\xE9rdame",
  "login.secureYourAdventure": "Asegura Tu Aventura",
  "login.discoverBali": "Descubre la magia de Bali con nuestros paquetes de viaje exclusivos.",
  "login.exploreHiddenGems": "Explora Joyas Ocultas",
  "login.uncoverDestinations": "Descubre destinos incre\xEDbles esperando ser explorados.",
  "login.adventureAwaits": "La Aventura Te Espera",
  "login.fromMountainPeaks": "Desde cimas de monta\xF1as hasta profundidades del oc\xE9ano, tu viaje comienza aqu\xED.",
  "login.emailOrPhone": "Correo o Tel\xE9fono",
  "login.emailOrPhonePlaceholder": "Ingresa tu correo electr\xF3nico",
  "login.password": "Contrase\xF1a",
  "login.passwordPlaceholder": "Ingresa tu contrase\xF1a",
  "login.forgotPassword": "\xBFOlvidaste tu contrase\xF1a?",
  "login.signInError": "Error al iniciar sesi\xF3n. Por favor intenta de nuevo.",
  "login.continue": "Continuar",
  "login.signingIn": "Iniciando sesi\xF3n...",
  "login.logIn": "Iniciar Sesi\xF3n",
  "login.sign_in_with_google": "Iniciar sesi\xF3n con Google",
  "login.sign_in_with_facebook": "Iniciar sesi\xF3n con Facebook",
  "login.sign_in_with_apple": "Iniciar sesi\xF3n con Apple",
  "login.dontHaveAccount": "\xBFNo tienes una cuenta?",
  "login.signUp": "Reg\xEDstrate",
  "login.whatsYourEmail": "\xBFCu\xE1l es tu email?",
  "login.accessYourAccount": "Accede a tu cuenta",
  "login.disclaimerPrefix": "Al continuar en esta p\xE1gina, aceptas nuestros",
  "login.termsOfUse": "T\xE9rminos de Uso",
  "login.disclaimerMiddle": "y comprendes que tu informaci\xF3n ser\xE1 utilizada como se describe en nuestra",
  "login.privacyPolicy": "Pol\xEDtica de Privacidad",
  "login.or": "o",
  "login.signInWithPasskey": "Ingresar con Passkey",
  "login.heroTitle": "\xA1\xDAnete a Ticket\xF3n!",
  "login.heroSubtitle": "\xA1Los eventos que amas, a la vuelta de la esquina!",
  "signup.joinAdventure": "\xDAnete a la Aventura",
  "signup.discoverWorld": "Crea tu cuenta y descubre el mundo con nosotros.",
  "signup.exploreWithUs": "Explora Con Nosotros",
  "signup.createYourJourney": "Crea tu viaje y haz recuerdos inolvidables.",
  "signup.startYourStory": "Comienza Tu Historia",
  "signup.memoriesAwait": "Recuerdos incre\xEDbles te esperan. Reg\xEDstrate y comienza tu aventura.",
  "signup.firstName": "Nombre",
  "signup.firstNamePlaceholder": "Nombre",
  "signup.lastName": "Apellido",
  "signup.lastNamePlaceholder": "Apellido",
  "signup.email": "Correo Electr\xF3nico",
  "signup.emailPlaceholder": "Ingresa tu correo electr\xF3nico",
  "signup.password": "Contrase\xF1a",
  "signup.passwordPlaceholder": "Crea una contrase\xF1a",
  "signup.confirmPassword": "Confirmar Contrase\xF1a",
  "signup.confirmPasswordPlaceholder": "Confirma tu contrase\xF1a",
  "signup.acceptTerms": "Acepto los",
  "signup.termsOfService": "T\xE9rminos de Servicio",
  "signup.creatingAccount": "Creando cuenta...",
  "signup.createAccount": "Crear Cuenta",
  "signup.finishCreatingAccount": "Finalizar registro con {provider}",
  "signup.finishingAccount": "Finalizando registro...",
  "signup.alreadyHaveAccount": "\xBFYa tienes una cuenta?",
  "signup.signIn": "Iniciar sesi\xF3n",
  "signup.passwordMismatch": "Las contrase\xF1as no coinciden",
  "signup.termsRequired": "Debes aceptar los t\xE9rminos",
  "signup.passwordsDoNotMatch": "Las contrase\xF1as no coinciden",
  "signup.signUpError": "Error al registrarse",
  "signup.tryAgainLater": "Algo sali\xF3 mal. Por favor intenta m\xE1s tarde",
  "signup.heroTitle": "\xA1\xDAnete a Ticket\xF3n!",
  "signup.heroSubtitle": "\xA1Crea tu cuenta y comienza tu aventura!",
  "signup.createYourAccount": "Crea tu cuenta",
  "signup.completeYourProfile": "Completa tu perfil de {provider}",
  "signup.completeProfileDescription": "Solo unos datos m\xE1s para crear tu cuenta con {provider}",
  "signup.disclaimerPrefix": "Al continuar en esta p\xE1gina, aceptas nuestros",
  "signup.termsOfUse": "T\xE9rminos de Uso",
  "signup.disclaimerMiddle": "y comprendes que tu informaci\xF3n ser\xE1 utilizada como se describe en nuestra",
  "signup.privacyPolicy": "Pol\xEDtica de Privacidad",
  "countries.us": "Estados Unidos",
  "countries.ca": "Canad\xE1",
  "countries.mx": "M\xE9xico",
  "countries.br": "Brasil",
  "countries.ar": "Argentina",
  "countries.co": "Colombia",
  "countries.pe": "Per\xFA",
  "countries.ve": "Venezuela",
  "countries.cl": "Chile",
  "countries.ec": "Ecuador",
  "countries.gt": "Guatemala",
  "countries.cu": "Cuba",
  "countries.sv": "El Salvador",
  "countries.gb": "Reino Unido",
  "countries.fr": "Francia",
  "countries.de": "Alemania",
  "countries.it": "Italia",
  "countries.es": "Espa\xF1a",
  "countries.nl": "Pa\xEDses Bajos",
  "countries.se": "Suecia",
  "countries.no": "Noruega",
  "countries.ch": "Suiza",
  "countries.be": "B\xE9lgica",
  "countries.pl": "Polonia",
  "countries.pt": "Portugal",
  "countries.ie": "Irlanda",
  "countries.gr": "Grecia",
  "countries.dk": "Dinamarca",
  "signup.passwordRequired": "La contrase\xF1a es requerida",
  "signup.passwordNeedsNumber": "La contrase\xF1a debe contener al menos un n\xFAmero",
  "signup.passwordNeedsSpecial": "La contrase\xF1a debe contener al menos un car\xE1cter especial",
  "signup.confirmPasswordRequired": "Por favor confirma tu contrase\xF1a",
  "signup.phoneCountryCode": "C\xF3digo de Pa\xEDs",
  "signup.phone": "Tel\xE9fono",
  "signup.phonePlaceholder": "Ingresa tu n\xFAmero de tel\xE9fono",
  "signup.phoneCountryCodePlaceholder": "Selecciona el c\xF3digo de pa\xEDs",
  "signup.phoneRequired": "El n\xFAmero de tel\xE9fono es requerido",
  "signup.phoneCountryCodeRequired": "El c\xF3digo de pa\xEDs es requerido",
  "signup.invalidPhoneNumeric": "El n\xFAmero de tel\xE9fono solo debe contener d\xEDgitos.",
  "signup.invalidPhoneLength": "El n\xFAmero de tel\xE9fono debe tener exactamente {digits} d\xEDgitos para este pa\xEDs.",
  "signup.recaptchaError": "Error de reCAPTCHA. Intenta nuevamente.",
  "signup.firstNameRequired": "El nombre es requerido",
  "signup.invalidFirstName": "El nombre solo puede contener letras, espacios, guiones y ap\xF3strofes",
  "signup.lastNameRequired": "El apellido es requerido",
  "signup.invalidLastName": "El apellido solo puede contener letras, espacios, guiones y ap\xF3strofes",
  "signup.emailRequired": "El correo electr\xF3nico es requerido",
  "signup.invalidEmail": "Formato de correo electr\xF3nico inv\xE1lido",
  "forgotPassword.forgotPassword": "Olvid\xE9 mi Contrase\xF1a",
  "forgotPassword.enterEmailToReset": "Ingresa tu correo electr\xF3nico y te enviaremos instrucciones para restablecer tu contrase\xF1a.",
  "forgotPassword.email": "Correo Electr\xF3nico",
  "forgotPassword.emailPlaceholder": "Ingresa tu correo electr\xF3nico",
  "forgotPassword.sendingEmail": "Enviando...",
  "forgotPassword.sendResetEmail": "Enviar Correo de Restablecimiento",
  "forgotPassword.sendEmailError": "Error al enviar el correo. Por favor intenta de nuevo.",
  "forgotPassword.emailSent": "\xA1Correo Enviado!",
  "forgotPassword.checkYourEmail": "Si existe una cuenta con este correo, se ha enviado un c\xF3digo de restablecimiento.",
  "forgotPassword.emailInstructions": "Si no ves el correo, revisa tu carpeta de spam o intenta de nuevo.",
  "forgotPassword.backToLogin": "Volver al Inicio de Sesi\xF3n",
  "forgotPassword.rememberPassword": "\xBFRecuerdas tu contrase\xF1a?",
  "otpVerification.verifyCode": "Verificar C\xF3digo",
  "otpVerification.enterCodeSent": "Ingresa el c\xF3digo de 6 d\xEDgitos que enviamos a tu correo.",
  "otpVerification.verificationCode": "C\xF3digo de Verificaci\xF3n",
  "otpVerification.verifying": "Verificando...",
  "otpVerification.incompleteCode": "Por favor ingresa el c\xF3digo completo de 6 d\xEDgitos.",
  "otpVerification.verificationError": "Error de verificaci\xF3n. Por favor intenta de nuevo.",
  "otpVerification.didntReceiveCode": "\xBFNo recibiste el c\xF3digo?",
  "otpVerification.sending": "Enviando...",
  "otpVerification.resendIn": "Reenviar en",
  "otpVerification.resendCode": "Reenviar C\xF3digo",
  "otpVerification.resendError": "Error al reenviar el c\xF3digo. Por favor intenta de nuevo.",
  "otpVerification.wrongEmail": "\xBFCorreo incorrecto?",
  "otpVerification.backToLogin": "Volver al Inicio de Sesi\xF3n",
  "resetPassword.resetPassword": "Restablecer Contrase\xF1a",
  "resetPassword.enterNewPassword": "Ingresa tu nueva contrase\xF1a abajo.",
  "resetPassword.newPassword": "Nueva Contrase\xF1a",
  "resetPassword.newPasswordPlaceholder": "Ingresa nueva contrase\xF1a",
  "resetPassword.confirmNewPassword": "Confirmar Nueva Contrase\xF1a",
  "resetPassword.confirmNewPasswordPlaceholder": "Confirma nueva contrase\xF1a",
  "resetPassword.updatingPassword": "Actualizando...",
  "resetPassword.updatePassword": "Actualizar Contrase\xF1a",
  "resetPassword.passwordMismatch": "Las contrase\xF1as no coinciden",
  "resetPassword.passwordTooShort": "La contrase\xF1a debe tener al menos 8 caracteres",
  "resetPassword.resetError": "Error al restablecer la contrase\xF1a. Por favor intenta de nuevo.",
  "resetPassword.passwordResetSuccess": "\xA1Contrase\xF1a Restablecida!",
  "resetPassword.passwordUpdated": "Tu contrase\xF1a ha sido actualizada exitosamente.",
  "resetPassword.youCanNowLogin": "Ahora puedes iniciar sesi\xF3n con tu nueva contrase\xF1a.",
  "resetPassword.continueToLogin": "Continuar al Inicio de Sesi\xF3n",
  "resetPassword.rememberPassword": "\xBFRecuerdas tu contrase\xF1a?",
  "resetPassword.backToLogin": "Volver al Inicio de Sesi\xF3n",
  "resetPassword.passwordsDoNotMatch": "Las contrase\xF1as no coinciden",
  "resetPassword.email": "Correo Electr\xF3nico",
  "passwordStrength.passwordStrength": "Seguridad de la Contrase\xF1a",
  "passwordStrength.weak": "D\xE9bil",
  "passwordStrength.medium": "Media",
  "passwordStrength.strong": "Fuerte",
  "passwordStrength.atLeast8Chars": "Al menos 8 caracteres",
  "passwordStrength.lowercaseLetter": "Letra min\xFAscula",
  "passwordStrength.uppercaseLetter": "Letra may\xFAscula",
  "passwordStrength.number": "N\xFAmero",
  "passwordStrength.specialCharacter": "Car\xE1cter especial",
  "accountLinking.heroTitle": "Vincula Tu Cuenta",
  "accountLinking.heroSubtitle": "Conecta tu cuenta de {provider}",
  "accountLinking.accountFound": "Cuenta Encontrada",
  "accountLinking.existingAccountMessage": "Ya existe una cuenta con este correo electr\xF3nico. \xBFTe gustar\xEDa vincularla con {provider}?",
  "accountLinking.email": "Correo Electr\xF3nico",
  "accountLinking.linkingExplanation": "Al continuar, tu cuenta existente se vincular\xE1 con {provider}. Podr\xE1s iniciar sesi\xF3n con {provider} o con tu correo y contrase\xF1a.",
  "accountLinking.continueWithProvider": "Continuar con {provider}",
  "accountLinking.loginWithEmail": "Iniciar sesi\xF3n con correo",
  "accountLinking.linking": "Vinculando con {provider}...",
  "accountLinking.linkingError": "Error al vincular con {provider}. Por favor intenta de nuevo.",
  "accountLinking.securityNote": "La seguridad de tu cuenta es nuestra prioridad. Esta acci\xF3n conectar\xE1 tu cuenta existente con {provider}."
};

// src/providers/LocalizationProvider.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var translations = {
  en: en_default,
  es: es_default
};
var LocalizationContext = createContext2(
  null
);
function LocalizationProvider({
  children,
  locale = "es"
}) {
  const t = useMemo2(() => {
    return (key, params) => {
      let translation = translations[locale]?.[key] ?? key;
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          translation = translation.replace(`{${paramKey}}`, paramValue);
        });
      }
      return translation;
    };
  }, [locale]);
  const value = useMemo2(
    () => ({
      locale,
      t
    }),
    [locale, t]
  );
  return /* @__PURE__ */ jsx2(LocalizationContext.Provider, { value, children });
}
function useLocalization() {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      "useLocalization must be used within a LocalizationProvider"
    );
  }
  return context;
}

// src/providers/AuthenticationCoreProvider.tsx
import { ApolloProvider } from "@apollo/client/react";

// src/graphql/authenticationClient.ts
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { SetContextLink } from "@apollo/client/link/context";
var apolloClient = null;
function createAuthenticationClient(config) {
  const httpLink = new HttpLink({
    uri: config.uri
  });
  const authLink = new SetContextLink(async ({ headers }) => {
    let extra = {};
    try {
      if (config.getAuthHeader) {
        extra = await config.getAuthHeader();
      } else if (config.headers) {
        extra = config.headers;
      }
    } catch {
    }
    return {
      headers: {
        ...headers || {},
        ...extra
      }
    };
  });
  return new ApolloClient({
    link: ApolloLink.from([authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network"
      }
    }
  });
}
function getAuthenticationClient(config) {
  if (!apolloClient) {
    apolloClient = createAuthenticationClient(config);
  }
  return apolloClient;
}
function resetAuthenticationClient() {
  apolloClient = null;
}

// src/providers/AuthenticationCoreProvider.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function AuthenticationCoreProvider({
  children,
  graphqlConfig,
  locale = "es",
  initialAuthState,
  socialSignIn,
  sessionBridge
}) {
  const apolloClient2 = getAuthenticationClient(graphqlConfig);
  return /* @__PURE__ */ jsx3(ApolloProvider, { client: apolloClient2, children: /* @__PURE__ */ jsx3(LocalizationProvider, { locale, children: /* @__PURE__ */ jsx3(AuthProvider, { initialState: initialAuthState, socialSignIn, sessionBridge, children }) }) });
}

// src/hooks/useAuth.ts
import { useContext as useContext2 } from "react";
function useAuth() {
  const context = useContext2(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// src/hooks/useLogin.ts
import { CombinedGraphQLErrors } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

// src/graphql/mutations/index.ts
import { gql } from "@apollo/client";
var LOGIN_MUTATION = gql`
   mutation login(
    $email: String!
    $authProviderType: AuthProviderType!
    $keepSessionAlive: Boolean = false
    $accessToken: String
    $password: String
  ) {
    login(
      email: $email
      authProviderType: $authProviderType
      keepSessionAlive: $keepSessionAlive
      accessToken: $accessToken
      password: $password
    ) {
      token
      legacyToken
      userExternalId
      expiresAt
      user {
        email
        firstName
        lastName
        externalId
        phoneCountryCode
        phone
        avatarUrl
        authProviderType
        roles {
          externalId
          name
        }
      }
    }
  }
`;
var SIGNUP_MUTATION = gql`
  mutation register(
    $authProviderType: AuthProviderType!
    $countryAbbreviation: CountryAbbreviation!
    $phone: String!
    $reCaptchaToken: String!
    $email: String
    $firstName: String
    $lastName: String
    $accessToken: String
    $password: String
    $passwordConfirmation: String
  ) {
    register(
      authProviderType: $authProviderType
      countryAbbreviation: $countryAbbreviation
      phone: $phone
      reCaptchaToken: $reCaptchaToken
      email: $email
      firstName: $firstName
      lastName: $lastName
      accessToken: $accessToken
      password: $password
      passwordConfirmation: $passwordConfirmation
    ) {
      token
      legacyToken
      userExternalId
      expiresAt
      user {
        email
        firstName
        lastName
        externalId
        phoneCountryCode
        phone
        avatarUrl
        authProviderType
        roles {
          externalId
          name
        }
      }
    }
  }
`;
var LOGOUT_MUTATION = gql`
  mutation Logout {
    logout
  }
`;
var FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email)
  }
`;
var RESET_PASSWORD_MUTATION = gql`
  mutation resetPassword(
    $email: String!
    $authProviderType: AuthProviderType!
    $newPassword: String!
    $passwordConfirmation: String!
    $resetCode: String!
  ) {
    resetPassword(
      email: $email
      authProviderType: $authProviderType
      newPassword: $newPassword
      passwordConfirmation: $passwordConfirmation
      resetCode: $resetCode
    )
  }
`;
var REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      accessToken
      refreshToken
    }
  }
`;
var LINK_ACCOUNT_MUTATION = gql`
  mutation linkAccount(
    $email: String!
    $authProviderType: AuthProviderType!
    $accessToken: String!
  ) {
    linkAccount(
      email: $email
      authProviderType: $authProviderType
      accessToken: $accessToken
    ) {
      token
      legacyToken
      userExternalId
      expiresAt
      user {
        email
        firstName
        lastName
        externalId
        phoneCountryCode
        phone
        avatarUrl
        authProviderType
        roles {
          externalId
          name
        }
      }
    }
  }
`;

// src/hooks/useLogin.ts
function useLogin() {
  const [loginMutation, { loading, error, data }] = useMutation(LOGIN_MUTATION);
  const login = async (input) => {
    try {
      const result = await loginMutation({
        variables: {
          email: input.email,
          authProviderType: input.authProviderType,
          password: input.password,
          keepSessionAlive: input.keepSessionAlive,
          accessToken: input.accessToken
        }
      });
      if (!result.data?.login) {
        throw new Error("LOGIN_FAILED");
      }
      return result.data.login;
    } catch (error2) {
      if (CombinedGraphQLErrors.is(error2) && error2.errors.length > 0) {
        throw new Error(error2.errors[0].message);
      }
      throw error2;
    }
  };
  return {
    login,
    isLoading: loading,
    error,
    data: data?.login ?? void 0
  };
}

// src/hooks/useSignup.ts
import { useMutation as useMutation2 } from "@apollo/client/react";
function useSignup() {
  const [signupMutation, { loading, error, data }] = useMutation2(SIGNUP_MUTATION);
  const signup = async (input) => {
    const result = await signupMutation({
      variables: {
        authProviderType: input.authProviderType,
        countryAbbreviation: input.phoneCountryCode,
        phone: input.phone,
        reCaptchaToken: input.reCaptchaToken,
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
        accessToken: input.accessToken,
        password: input.password,
        passwordConfirmation: input.confirmPassword
      }
    });
    return result.data?.register;
  };
  return {
    signup,
    isLoading: loading,
    error,
    data: data?.register ?? void 0
  };
}

// src/hooks/useLogout.ts
import { useMutation as useMutation3 } from "@apollo/client/react";
function useLogout() {
  const [logoutMutation, { loading, error, data }] = useMutation3(LOGOUT_MUTATION);
  const logout = async () => {
    const result = await logoutMutation();
    return { value: result.data?.logout || "" };
  };
  return {
    logout,
    isLoading: loading,
    error,
    data: data?.logout ? { value: data.logout } : void 0
  };
}

// src/hooks/useForgotPassword.ts
import { CombinedGraphQLErrors as CombinedGraphQLErrors2 } from "@apollo/client";
import { useMutation as useMutation4 } from "@apollo/client/react";
function useForgotPassword() {
  const [forgotPasswordMutation, { loading, error }] = useMutation4(
    FORGOT_PASSWORD_MUTATION
  );
  const forgotPassword = async (email) => {
    try {
      await forgotPasswordMutation({
        variables: { email }
      });
    } catch (error2) {
      const firstGraphQLError = CombinedGraphQLErrors2.is(error2) ? error2.errors[0] : void 0;
      if (firstGraphQLError) {
        throw new Error(firstGraphQLError.message);
      }
      throw error2;
    }
  };
  return {
    forgotPassword,
    isLoading: loading,
    error
  };
}

// src/hooks/useResetPassword.ts
import { useMutation as useMutation5 } from "@apollo/client/react";
function useResetPassword() {
  const [resetPasswordMutation, { loading, error, data }] = useMutation5(
    RESET_PASSWORD_MUTATION
  );
  const resetPassword = async (input) => {
    const result = await resetPasswordMutation({
      variables: {
        email: input.email,
        authProviderType: input.authProviderType,
        newPassword: input.password,
        passwordConfirmation: input.confirmPassword,
        resetCode: input.resetCode
      }
    });
    return result.data?.resetPassword;
  };
  return {
    resetPassword,
    isLoading: loading,
    error,
    data: data?.resetPassword
  };
}

// src/hooks/useLoginMethod.ts
import { useLazyQuery } from "@apollo/client/react";
import { useCallback as useCallback2 } from "react";

// src/graphql/queries/index.ts
import { gql as gql2 } from "@apollo/client";
var GET_ME_QUERY = gql2`
  query GetMe {
    me {
      id
      email
      firstName
      lastName
      createdAt
      updatedAt
    }
  }
`;
var VERIFY_TOKEN_QUERY = gql2`
  query VerifyToken($token: String!) {
    verifyToken(token: $token) {
      valid
      userId
      expiresAt
    }
  }
`;
var CHECK_EMAIL_EXISTS_QUERY = gql2`
  query CheckEmailExists($email: String!) {
    checkEmailExists(email: $email) {
      exists
    }
  }
`;
var GET_LOGIN_METHOD_QUERY = gql2`
  query LoginMethod($email: String!) {
    loginMethod(email: $email) {
      didSucceed
      errorCode
      value {
        loginUrl
        label
        helper
        authProviderType
      }
    }
  }
`;

// src/hooks/useLoginMethod.ts
function useLoginMethod() {
  const [queryLoginMethod, { loading, error }] = useLazyQuery(GET_LOGIN_METHOD_QUERY);
  const getLoginMethod = useCallback2(async (email) => {
    try {
      const result = await queryLoginMethod({ variables: { email } });
      if (result.error || !result.data?.loginMethod) {
        return { didSucceed: false, errorCode: "LOGIN_METHOD_UNAVAILABLE" };
      }
      return result.data.loginMethod;
    } catch {
      return { didSucceed: false, errorCode: "LOGIN_METHOD_UNAVAILABLE" };
    }
  }, [queryLoginMethod]);
  return {
    getLoginMethod,
    isLoading: loading,
    error
  };
}

// src/hooks/useLinkAccount.ts
import { useMutation as useMutation6 } from "@apollo/client/react";
function useLinkAccount() {
  const [linkAccountMutation, { loading, error }] = useMutation6(
    LINK_ACCOUNT_MUTATION
  );
  const linkAccount = async (input) => {
    const result = await linkAccountMutation({
      variables: {
        email: input.email,
        authProviderType: input.authProviderType,
        accessToken: input.accessToken
      }
    });
    return result.data?.linkAccount ?? null;
  };
  return {
    linkAccount,
    isLoading: loading,
    error
  };
}

// src/hooks/useAuthHandlers.ts
import { useState as useState2, useCallback as useCallback3 } from "react";
var noop = () => {
};
var noopAuthSuccess = (_user) => {
};
function useAuthHandlers(config) {
  const {
    onNavigate,
    onAuthSuccess = noopAuthSuccess,
    onBackToHome = noop,
    successRedirectPath = "/"
  } = config;
  const { t } = useLocalization();
  const { login, isLoading: loginLoading } = useLogin();
  const { signup, isLoading: signupLoading } = useSignup();
  const { forgotPassword, isLoading: forgotPasswordLoading } = useForgotPassword();
  const { resetPassword: resetPasswordMutation, isLoading: resetPasswordLoading } = useResetPassword();
  const { getLoginMethod, isLoading: loginMethodLoading } = useLoginMethod();
  const { linkAccount, isLoading: linkAccountLoading } = useLinkAccount();
  const { setUser, setTokens, socialSignIn, sessionBridge } = useAuth();
  const [route, setRoute] = useState2("login");
  const [email, setEmail] = useState2("");
  const [emailSubmitted, setEmailSubmitted] = useState2(false);
  const [authMethod, setAuthMethod] = useState2(null);
  const [isLocalLoading, setIsLocalLoading] = useState2(false);
  const [otpToken, setOtpToken] = useState2("");
  const isLoading = loginLoading || signupLoading || forgotPasswordLoading || resetPasswordLoading || loginMethodLoading || linkAccountLoading || isLocalLoading;
  const resetEmailState = useCallback3(() => {
    setEmailSubmitted(false);
    setAuthMethod(null);
  }, []);
  const handleNavigate = useCallback3((targetRoute, params) => {
    onNavigate(targetRoute, params);
  }, [onNavigate]);
  const handleBack = useCallback3(() => {
    if (route === "login" && emailSubmitted) {
      resetEmailState();
    } else if (route === "login") {
      onBackToHome();
    } else {
      handleNavigate("login");
    }
  }, [route, emailSubmitted, resetEmailState, onBackToHome, handleNavigate]);
  const handleEmailSubmit = useCallback3(async (emailValue) => {
    setIsLocalLoading(true);
    try {
      const result = await getLoginMethod(emailValue);
      if (result.didSucceed && result.value) {
        const provider = result.value.authProviderType;
        if (provider === "GOOGLE") {
          await socialSignIn("google", successRedirectPath);
          return { success: true };
        }
        if (provider === "FACEBOOK") {
          await socialSignIn("facebook", successRedirectPath);
          return { success: true };
        }
        if (provider === "APPLE") {
          await socialSignIn("apple", successRedirectPath);
          return { success: true };
        }
        setAuthMethod("password");
        setEmailSubmitted(true);
        return { success: true };
      }
      setAuthMethod(null);
      setEmailSubmitted(false);
      const errorMessage = result.errorCode === "LOGIN_METHOD_UNAVAILABLE" ? t("login.signInError") : t("invalidCredentials");
      return { success: false, error: errorMessage };
    } catch {
      setEmailSubmitted(false);
      setAuthMethod(null);
      return { success: false, error: t("login.signInError") };
    } finally {
      setIsLocalLoading(false);
    }
  }, [getLoginMethod, socialSignIn, successRedirectPath, t]);
  const handleEmailChangeDetection = useCallback3(async (emailValue) => {
    try {
      const result = await getLoginMethod(emailValue);
      if (result?.didSucceed && result.value) {
        const provider = result.value.authProviderType;
        const method = provider === "TICKETON" ? "password" : provider.toLowerCase();
        setAuthMethod(method);
      } else {
        setAuthMethod(null);
      }
    } catch {
      setAuthMethod(null);
    }
  }, [getLoginMethod]);
  const handleLogin = useCallback3(async (emailValue, authProviderType, password, keepSessionAlive) => {
    try {
      const result = await login({ email: emailValue, authProviderType, password, keepSessionAlive });
      if (!result?.token) {
        return { success: false, error: t("invalidCredentials") || "Invalid credentials" };
      }
      setUser(result.user);
      setTokens(result.token, result.refreshToken);
      if (sessionBridge) {
        await sessionBridge.set({
          token: result.token,
          tokenExpiration: Date.parse(result.expiresAt),
          user: {
            email: result.user.email,
            firstName: result.user.firstName,
            lastName: result.user.lastName,
            externalId: result.user.externalId,
            phoneCountryCode: result.user.phoneCountryCode,
            phone: result.user.phone,
            avatarUrl: result.user.avatarUrl,
            authProviderType: result.user.authProviderType,
            roles: result.user.roles
          },
          keepSessionAlive
        });
      }
      onAuthSuccess(result.user);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error && error.message !== "LOGIN_FAILED" ? error.message : t("loginError");
      return { success: false, error: errorMessage };
    }
  }, [login, setUser, setTokens, sessionBridge, onAuthSuccess, t]);
  const handleSignUp = useCallback3(async (emailValue, authProviderType, password, confirmPassword, firstName, lastName, phone, phoneCountryCode, acceptTerms, reCaptchaToken) => {
    if (!acceptTerms) {
      return { success: false, error: t("signup.termsRequired") };
    }
    if (password !== confirmPassword) {
      return { success: false, error: t("signup.passwordsDoNotMatch") };
    }
    try {
      const result = await signup({
        email: emailValue,
        authProviderType,
        password,
        confirmPassword,
        firstName,
        lastName,
        phone,
        phoneCountryCode,
        reCaptchaToken
      });
      if (result?.token) {
        setUser(result.user);
        setTokens(result.token, null, result.legacyToken);
        if (sessionBridge) {
          await sessionBridge.set({
            token: result.token,
            tokenExpiration: result.expiresAt ? Date.parse(result.expiresAt) : Date.now() + 36e5,
            user: {
              email: result.user.email,
              firstName: result.user.firstName,
              lastName: result.user.lastName,
              externalId: result.user.externalId,
              phoneCountryCode: result.user.phoneCountryCode,
              phone: result.user.phone,
              avatarUrl: result.user.avatarUrl,
              authProviderType: result.user.authProviderType,
              roles: result.user.roles
            }
          });
        }
        onAuthSuccess(result.user);
        return { success: true };
      }
      return { success: false, error: t("signup.signUpError") };
    } catch {
      return { success: false, error: t("signup.signUpError") };
    }
  }, [signup, setUser, setTokens, onAuthSuccess, t]);
  const handleSocialSignUpComplete = useCallback3(async (accessToken, emailValue, authProviderType, firstName, lastName, phone, phoneCountryCode, acceptTerms, reCaptchaToken) => {
    if (!acceptTerms) {
      return { success: false, error: t("signup.termsRequired") };
    }
    try {
      const result = await signup({
        email: emailValue,
        authProviderType,
        firstName,
        lastName,
        phone,
        phoneCountryCode,
        reCaptchaToken,
        accessToken
      });
      if (result?.token) {
        setUser(result.user);
        setTokens(result.token, null, result.legacyToken);
        if (sessionBridge) {
          await sessionBridge.set({
            token: result.token,
            tokenExpiration: result.expiresAt ? Date.parse(result.expiresAt) : Date.now() + 36e5,
            user: {
              email: result.user.email,
              firstName: result.user.firstName,
              lastName: result.user.lastName,
              externalId: result.user.externalId,
              phoneCountryCode: result.user.phoneCountryCode,
              phone: result.user.phone,
              avatarUrl: result.user.avatarUrl,
              authProviderType: result.user.authProviderType,
              roles: result.user.roles
            }
          });
        }
        onAuthSuccess(result.user);
        return { success: true };
      }
      return { success: false, error: t("signup.signUpError") };
    } catch (error) {
      console.error("Social signup error:", error);
      const errorMessage = error instanceof Error ? error.message : t("signup.signUpError");
      return { success: false, error: errorMessage };
    }
  }, [signup, setUser, setTokens, sessionBridge, onAuthSuccess, t]);
  const handleSendResetEmail = useCallback3(async (emailValue) => {
    try {
      await forgotPassword(emailValue);
      setEmail(emailValue);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error && error.message !== "FORGOT_PASSWORD_FAILED" ? error.message : t("forgotPassword.sendEmailError");
      return { success: false, error: errorMessage };
    }
  }, [forgotPassword, handleNavigate, t]);
  const handleVerifyOTP = useCallback3(async (otp) => {
    setOtpToken(otp);
    handleNavigate("validate-forgot-password", { email });
    return { success: true };
  }, [email, handleNavigate]);
  const handleResendOTP = useCallback3(async () => {
    if (email) {
      await forgotPassword(email);
    }
    return { success: true };
  }, [email, forgotPassword]);
  const handleResetPassword = useCallback3(async (email2, authProviderType, password, confirmPassword, resetCode) => {
    if (password !== confirmPassword) {
      return { success: false, error: t("resetPassword.passwordsDoNotMatch") || "Passwords do not match" };
    }
    try {
      const result = await resetPasswordMutation({ email: email2, authProviderType, password, confirmPassword, resetCode });
      if (result) {
        return { success: true };
      }
      return { success: false, error: result || t("resetPassword.resetError") || "Failed to reset password" };
    } catch (error) {
      return { success: false, error: t("resetPassword.resetError") || "Failed to reset password" };
    }
  }, [otpToken, resetPasswordMutation, handleNavigate, t]);
  const handleGoogleSignIn = useCallback3(async () => {
    setIsLocalLoading(true);
    try {
      await socialSignIn("google", successRedirectPath);
    } finally {
      setIsLocalLoading(false);
    }
  }, [socialSignIn, successRedirectPath]);
  const handleFacebookSignIn = useCallback3(async () => {
    setIsLocalLoading(true);
    try {
      await socialSignIn("facebook", successRedirectPath);
    } finally {
      setIsLocalLoading(false);
    }
  }, [socialSignIn, successRedirectPath]);
  const handleAppleSignIn = useCallback3(async () => {
    setIsLocalLoading(true);
    try {
      await socialSignIn("apple", successRedirectPath);
    } finally {
      setIsLocalLoading(false);
    }
  }, [socialSignIn, successRedirectPath]);
  const handleAccountLinking = useCallback3(async (accessToken, emailValue, provider) => {
    try {
      const authProviderType = provider.toUpperCase();
      const result = await linkAccount({
        email: emailValue,
        authProviderType,
        accessToken
      });
      if (result?.token) {
        setUser(result.user);
        setTokens(result.token, result.legacyToken);
        if (sessionBridge) {
          await sessionBridge.set({
            token: result.token,
            tokenExpiration: result.expiresAt ? Date.parse(result.expiresAt) : Date.now() + 36e5,
            user: {
              email: result.user.email,
              firstName: result.user.firstName,
              lastName: result.user.lastName,
              externalId: result.user.externalId,
              phoneCountryCode: result.user.phoneCountryCode,
              phone: result.user.phone,
              avatarUrl: result.user.avatarUrl,
              authProviderType: result.user.authProviderType,
              roles: result.user.roles
            }
          });
        }
        onAuthSuccess(result.user);
        return { success: true };
      }
      const providerName = provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
      return { success: false, error: t("accountLinking.linkingError").replaceAll("{provider}", providerName) };
    } catch (error) {
      const providerName = provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase();
      const errorMessage = error instanceof Error ? error.message : t("accountLinking.linkingError").replaceAll("{provider}", providerName);
      return { success: false, error: errorMessage };
    }
  }, [linkAccount, setUser, setTokens, sessionBridge, onAuthSuccess, t]);
  return {
    route,
    setRoute,
    email,
    setEmail,
    emailSubmitted,
    authMethod,
    isLoading,
    t,
    handleLogin,
    handleEmailSubmit,
    handleEmailChangeDetection,
    handleSignUp,
    handleSocialSignUpComplete,
    handleSendResetEmail,
    handleVerifyOTP,
    handleResendOTP,
    handleResetPassword,
    handleGoogleSignIn,
    handleFacebookSignIn,
    handleAppleSignIn,
    handleAccountLinking,
    handleNavigate,
    handleBack,
    resetEmailState
  };
}

// src/hooks/usePageRestore.ts
import { useEffect as useEffect2, useRef } from "react";
function usePageRestore(onRestore) {
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  useEffect2(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        onRestoreRef.current();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);
}

// src/pages/LoginPage.tsx
import { useState as useState3, useEffect as useEffect3, useRef as useRef2 } from "react";
import {
  Button,
  Input,
  Label,
  AuthProviderButton
} from "ui";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Fragment, jsx as jsx4, jsxs } from "react/jsx-runtime";
var TERMS_URL = `https://ticketon.com/terms-of-use`;
var PRIVACY_URL = `https://ticketon.com/ticketon-privacy`;
function LoginPage({
  onLogin,
  onEmailSubmit,
  onGoogleSignIn,
  onFacebookSignIn,
  onAppleSignIn,
  onBack,
  onLogoClick,
  onSignUpClick,
  onForgotPasswordClick,
  logoUrl,
  t,
  heroImage = "/images/join-ticketon-login.webp",
  heroSubtitle,
  showHeroText = true,
  termsUrl,
  privacyUrl,
  emailSubmitted = false,
  authMethod = null,
  isLoading = false,
  showSignUpLink = true,
  initialError
}) {
  const [formData, setFormData] = useState3({
    email: "",
    password: "",
    keepSessionAlive: false
  });
  const [showPassword, setShowPassword] = useState3(false);
  const [error, setError] = useState3(initialError);
  const passwordInputRef = useRef2(null);
  const [submitIsLoading, setSubmitIsLoading] = useState3(false);
  const [loadingProvider, setLoadingProvider] = useState3(null);
  usePageRestore(() => {
    setLoadingProvider(null);
    setSubmitIsLoading(false);
  });
  useEffect3(() => {
    if (initialError) {
      setError(initialError);
    }
  }, [initialError]);
  useEffect3(() => {
    if (emailSubmitted && authMethod === "password") {
      passwordInputRef.current?.focus();
    }
  }, [emailSubmitted, authMethod]);
  const finalHeroSubtitle = heroSubtitle ?? t("login.heroSubtitle");
  const inputStyle = "w-full border border-ticketon-light-border rounded-2xl h-[60px] !p-6 !shadow-component font-inter !text-[13px] md:!text-[13px] !font-medium !leading-tight !tracking-normal !text-ticketon-gray-alt placeholder:!text-[13px] md:placeholder:!text-[13px] placeholder:!font-medium placeholder:!text-ticketon-gray-alt";
  const handleInputChange = (field) => (e) => {
    const value = field === "keepSessionAlive" ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    if (field === "email" && error) {
      setError(void 0);
    }
  };
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(void 0);
    setSubmitIsLoading(true);
    try {
      if (onEmailSubmit) {
        const result = await onEmailSubmit(formData.email);
        if (!result.success) {
          setError(result.error || t("login.signInError"));
        }
      }
    } finally {
      setSubmitIsLoading(false);
    }
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(void 0);
    setSubmitIsLoading(true);
    try {
      const result = await onLogin(
        formData.email,
        "TICKETON" /* TICKETON */,
        formData.password,
        formData.keepSessionAlive
      );
      if (!result.success) {
        setError(result.error);
      }
    } catch (error2) {
      setError(t("login.signInError") || error2.message);
    } finally {
      setSubmitIsLoading(false);
    }
  };
  const handleGoogleSignIn = () => {
    setLoadingProvider("google");
    onGoogleSignIn?.();
  };
  const handleFacebookSignIn = () => {
    setLoadingProvider("facebook");
    onFacebookSignIn?.();
  };
  const handleAppleSignIn = () => {
    setLoadingProvider("apple");
    onAppleSignIn?.();
  };
  const anyLoading = submitIsLoading || !!loadingProvider || isLoading;
  const getButtonText = () => {
    if (!emailSubmitted) {
      return t("login.continue");
    }
    if (authMethod === "password") {
      return submitIsLoading ? t("login.signingIn") : t("login.logIn");
    }
    return t("login.continue");
  };
  const getSubmitHandler = () => {
    if (!emailSubmitted) {
      return handleEmailSubmit;
    }
    if (authMethod === "password") {
      return handleLogin;
    }
    return handleEmailSubmit;
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex w-full min-h-[100dvh] flex-col lg:h-screen lg:max-h-screen lg:min-h-0 lg:overflow-hidden lg:flex-row", children: [
    /* @__PURE__ */ jsx4("div", { className: "flex h-16 shrink-0 items-center justify-center bg-white lg:hidden", children: logoUrl && /* @__PURE__ */ jsx4(
      "img",
      {
        src: logoUrl,
        alt: "Logo",
        className: "h-8 w-auto cursor-pointer",
        onClick: onLogoClick ?? onBack
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "relative h-52 w-full shrink-0 lg:hidden", children: [
      /* @__PURE__ */ jsx4("img", { src: heroImage, alt: "", className: "w-full rounded-t-3xl h-full object-cover" }),
      /* @__PURE__ */ jsx4("div", { className: "absolute inset-0" }),
      showHeroText && /* @__PURE__ */ jsxs("div", { className: "absolute inset-0 flex flex-col items-center justify-center px-6 text-center", children: [
        /* @__PURE__ */ jsx4("h2", { className: "text-white text-2xl font-bold mb-1", children: t("login.heroTitle") }),
        /* @__PURE__ */ jsx4("p", { className: "text-white text-base opacity-90", children: finalHeroSubtitle })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "relative z-10 -mt-6 flex flex-col rounded-t-3xl bg-white lg:mt-0 lg:basis-1/2 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:rounded-none", children: [
      /* @__PURE__ */ jsx4("div", { className: "hidden lg:flex items-center h-16 px-12", children: logoUrl && /* @__PURE__ */ jsx4(
        "img",
        {
          src: logoUrl,
          alt: "Logo",
          className: "h-8 w-auto cursor-pointer",
          onClick: onLogoClick ?? onBack
        }
      ) }),
      /* @__PURE__ */ jsx4("div", { className: "flex flex-col items-center px-6 pb-6 pt-2 lg:flex-1 lg:justify-center lg:pb-8 xl:px-12", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-6 w-full max-w-md lg:border pt-8 lg:px-6 pb-6 lg:rounded-3xl lg:shadow-component", children: [
        /* @__PURE__ */ jsx4("h1", { className: "text-base text-ticketon-lighter-blue font-bold text-center", children: emailSubmitted ? t("login.accessYourAccount") : t("login.whatsYourEmail") }),
        /* @__PURE__ */ jsxs("form", { className: "space-y-4", onSubmit: getSubmitHandler(), children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx4(Label, { htmlFor: "email", className: "sr-only", children: t("login.emailOrPhone") }),
            /* @__PURE__ */ jsx4(
              Input,
              {
                id: "email",
                type: "email",
                placeholder: t("login.emailOrPhonePlaceholder"),
                value: formData.email,
                onChange: handleInputChange("email"),
                className: inputStyle,
                autoComplete: "username",
                required: true,
                disabled: anyLoading
              }
            )
          ] }),
          emailSubmitted && authMethod === "password" && /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx4(Label, { htmlFor: "password", className: "sr-only", children: t("login.password") }),
            /* @__PURE__ */ jsx4(
              Input,
              {
                id: "password",
                ref: passwordInputRef,
                type: showPassword ? "text" : "password",
                placeholder: t("login.passwordPlaceholder"),
                value: formData.password,
                onChange: handleInputChange("password"),
                className: inputStyle,
                autoComplete: "current-password",
                required: true,
                disabled: anyLoading
              }
            ),
            /* @__PURE__ */ jsx4(
              "button",
              {
                type: "button",
                onClick: () => setShowPassword(!showPassword),
                className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700",
                children: showPassword ? /* @__PURE__ */ jsx4(FaEyeSlash, { size: 16 }) : /* @__PURE__ */ jsx4(FaEye, { size: 16 })
              }
            )
          ] }),
          emailSubmitted && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
            /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-2 text-sm text-gray-700", children: [
              /* @__PURE__ */ jsx4(
                "input",
                {
                  type: "checkbox",
                  checked: formData.keepSessionAlive,
                  onChange: handleInputChange("keepSessionAlive"),
                  className: "h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary",
                  disabled: anyLoading
                }
              ),
              /* @__PURE__ */ jsx4("span", { children: t("rememberMe") })
            ] }),
            /* @__PURE__ */ jsx4(
              "button",
              {
                type: "button",
                onClick: onForgotPasswordClick,
                className: "text-sm hover:underline text-ticketon-secondary-blue",
                disabled: anyLoading,
                children: t("login.forgotPassword")
              }
            )
          ] }),
          /* @__PURE__ */ jsx4(
            Button,
            {
              className: "w-full h-11 text-white font-medium hover:opacity-90 disabled:opacity-50 rounded-lg bg-ticketon-lighter-blue",
              type: "submit",
              disabled: submitIsLoading || !!loadingProvider,
              isLoading: submitIsLoading,
              children: getButtonText()
            }
          ),
          !emailSubmitted && (onGoogleSignIn || onFacebookSignIn || onAppleSignIn) && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
              /* @__PURE__ */ jsx4("div", { className: "flex-1 h-px bg-gray-200" }),
              /* @__PURE__ */ jsx4("span", { className: "text-sm text-gray-400", children: t("login.or") }),
              /* @__PURE__ */ jsx4("div", { className: "flex-1 h-px bg-gray-200" })
            ] }),
            onGoogleSignIn && /* @__PURE__ */ jsx4(
              AuthProviderButton,
              {
                provider: "google",
                label: t("login.sign_in_with_google"),
                onClick: handleGoogleSignIn,
                disabled: submitIsLoading || !!loadingProvider,
                isLoading: loadingProvider === "google",
                className: "!bg-ticketon-light-sky-blue border-none hover:!bg-ticketon-sky-blue !text-ticketon-gray-600"
              }
            ),
            onFacebookSignIn && /* @__PURE__ */ jsx4(
              AuthProviderButton,
              {
                provider: "facebook",
                label: t("login.sign_in_with_facebook"),
                onClick: handleFacebookSignIn,
                disabled: submitIsLoading || !!loadingProvider,
                isLoading: loadingProvider === "facebook",
                className: "bg-ticketon-light-sky-blue hover:bg-ticketon-sky-blue !text-ticketon-gray-600"
              }
            )
          ] })
        ] }),
        error && /* @__PURE__ */ jsx4("div", { className: "bg-red-50 border border-red-200 rounded-md p-3", children: /* @__PURE__ */ jsx4("p", { className: "text-red-600 text-sm text-center", children: error }) }),
        showSignUpLink && /* @__PURE__ */ jsxs("p", { className: "text-center text-gray-600 text-sm", children: [
          t("login.dontHaveAccount"),
          " ",
          /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: onSignUpClick,
              className: "hover:underline font-medium text-ticketon-secondary-blue",
              children: t("login.signUp")
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("p", { className: "text-center text-gray-400 text-xs leading-relaxed", children: [
          t("login.disclaimerPrefix"),
          " ",
          /* @__PURE__ */ jsx4(
            "a",
            {
              href: termsUrl || TERMS_URL,
              target: "_blank",
              rel: "noopener noreferrer",
              className: "underline hover:text-gray-600 cursor-pointer",
              children: t("login.termsOfUse")
            }
          ),
          " ",
          t("login.disclaimerMiddle"),
          " ",
          /* @__PURE__ */ jsx4(
            "a",
            {
              href: privacyUrl || PRIVACY_URL,
              target: "_blank",
              rel: "noopener noreferrer",
              className: "underline hover:text-gray-600 cursor-pointer",
              children: t("login.privacyPolicy")
            }
          )
        ] })
      ] }) })
    ] }),
    /* @__PURE__ */ jsx4("div", { className: "hidden lg:flex lg:basis-1/2 lg:min-h-0 lg:h-full relative p-6 bg-white", children: /* @__PURE__ */ jsxs("div", { className: "relative w-full h-full min-h-0 rounded-3xl overflow-hidden bg-black", children: [
      /* @__PURE__ */ jsx4(
        "img",
        {
          src: heroImage,
          alt: "",
          className: "absolute inset-0 h-full w-full object-contain object-center"
        }
      ),
      showHeroText && /* @__PURE__ */ jsx4("div", { className: "absolute bottom-12 left-0 right-0 flex justify-center p-10", children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-[470px] text-center", children: [
        /* @__PURE__ */ jsx4("h2", { className: "text-white text-5xl font-bold mb-2", children: t("login.heroTitle") }),
        /* @__PURE__ */ jsx4("p", { className: "text-white text-2xl opacity-90", children: finalHeroSubtitle })
      ] }) })
    ] }) })
  ] });
}

// src/pages/SignupPage.tsx
import React4, { useEffect as useEffect4, useState as useState4 } from "react";
import {
  Button as Button2,
  Input as Input2,
  Label as Label2,
  Checkbox,
  PasswordStrengthMeter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "ui";
import { FaEye as FaEye2, FaEyeSlash as FaEyeSlash2 } from "react-icons/fa";
import { COUNTRIES_PHONE_CODES, CountryAbbreviation } from "ui/src/enums/CountryCodes";
import { SELECT_STATES } from "ui/src/components/Select";
import { executeRecaptcha } from "@ticketon/shared-utils";
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var TERMS_URL2 = `https://ticketon.com/terms-of-use`;
var PRIVACY_URL2 = `https://ticketon.com/ticketon-privacy`;
var hasValidEmailFormat = (email) => {
  if (!email) return false;
  const trimmed = email.trim();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
};
var isValidName = (name) => {
  if (!name) return false;
  const trimmed = name.trim();
  if (!trimmed) return false;
  if (/\d/.test(trimmed)) return false;
  if (!/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(trimmed)) return false;
  return true;
};
var RECAPTCHA_ACTION = "signup";
var SIGNUP_INPUT_CLASS_NAME = "w-full border border-ticketon-light-border rounded-2xl h-[60px] !p-6 !shadow-component font-inter !text-[13px] md:!text-[13px] !font-medium !leading-tight !tracking-normal !text-ticketon-gray-alt placeholder:!text-[13px] md:placeholder:!text-[13px] placeholder:!font-medium placeholder:!text-ticketon-gray-alt";
function SignupPage({
  onSignUp,
  onSocialSignUpComplete,
  onBack,
  onLogoClick,
  onLoginClick,
  logoUrl,
  t,
  heroImage = "/images/join-ticketon-login.webp",
  heroSubtitle,
  termsUrl,
  privacyUrl,
  socialAuthData,
  initialEmail,
  showPasswordFields = true,
  extraFields,
  initialValues,
  disabledFields = [],
  title,
  submitLabel
}) {
  const isSocialCompletion = !!socialAuthData;
  const providerName = socialAuthData?.provider ? socialAuthData.provider.charAt(0).toUpperCase() + socialAuthData.provider.slice(1).toLowerCase() : "";
  const isFieldDisabled = (field) => disabledFields.includes(field);
  const requiresPassword = !isSocialCompletion && showPasswordFields;
  const [formData, setFormData] = useState4({
    email: socialAuthData?.email || initialEmail || "",
    password: "",
    confirmPassword: "",
    firstName: socialAuthData?.firstName || "",
    lastName: socialAuthData?.lastName || "",
    phone: "",
    phoneCountryCode: CountryAbbreviation.MEX
  });
  const [acceptTerms, setAcceptTerms] = useState4(false);
  const [showPassword, setShowPassword] = useState4(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState4(false);
  const [error, setError] = useState4();
  const [isLoading, setIsLoading] = useState4(false);
  const [phoneError, setPhoneError] = useState4();
  const [phoneCountryCodeError, setPhoneCountryCodeError] = useState4();
  const [firstNameError, setFirstNameError] = useState4();
  const [lastNameError, setLastNameError] = useState4();
  const [emailError, setEmailError] = useState4();
  useEffect4(() => {
    if (!initialValues) {
      return;
    }
    const definedValues = Object.fromEntries(
      Object.entries(initialValues).filter(([, value]) => value !== void 0)
    );
    setFormData((prev) => ({ ...prev, ...definedValues }));
  }, [initialValues]);
  const finalHeroSubtitle = heroSubtitle ?? t("signup.heroSubtitle");
  const inputStyle = SIGNUP_INPUT_CLASS_NAME;
  const inputStyleError = "w-full border border-red-500 rounded-2xl h-[60px] !p-6 !shadow-component font-inter !text-[13px] md:!text-[13px] !font-medium !leading-tight !tracking-normal !text-ticketon-gray-alt placeholder:!text-[13px] md:placeholder:!text-[13px] placeholder:!font-medium placeholder:!text-ticketon-gray-alt focus:border-red-500 focus:ring-red-500";
  const passwordRequirements = React4.useMemo(() => {
    const pwd = formData.password || "";
    const hasMinLength = pwd.length >= 8;
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
    const isValid = hasMinLength && hasNumber && hasSpecial;
    return { isValid, hasMinLength, hasNumber, hasSpecial };
  }, [formData.password]);
  const handleInputChange = (field) => (e) => {
    const value = field === "phoneCountryCode" ? e.target.value : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    if (field === "phone" && phoneError) {
      setPhoneError(void 0);
    }
    if (field === "phoneCountryCode" && phoneCountryCodeError) {
      setPhoneCountryCodeError(void 0);
    }
    if (field === "firstName" && firstNameError) {
      setFirstNameError(void 0);
    }
    if (field === "lastName" && lastNameError) {
      setLastNameError(void 0);
    }
    if (field === "email" && emailError) {
      setEmailError(void 0);
    }
  };
  const handleCountryChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phoneCountryCode: value
    }));
    if (phoneCountryCodeError) {
      setPhoneCountryCodeError(void 0);
    }
    if (phoneError) {
      setPhoneError(void 0);
    }
  };
  const selectedCountry = COUNTRIES_PHONE_CODES.find(
    (c) => c.abbreviation === formData.phoneCountryCode
  );
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(void 0);
    setPhoneError(void 0);
    setPhoneCountryCodeError(void 0);
    setFirstNameError(void 0);
    setLastNameError(void 0);
    setEmailError(void 0);
    const trimmedFirstName = formData.firstName.trim();
    const trimmedLastName = formData.lastName.trim();
    const trimmedEmail = formData.email.trim();
    if (requiresPassword) {
      if (formData.password !== formData.confirmPassword) {
        setError(t("signup.passwordMismatch"));
        return;
      }
      if (!formData.password) {
        setError(t("signup.passwordRequired"));
        return;
      }
      if (!passwordRequirements.isValid) {
        if (!passwordRequirements.hasMinLength) {
          setError(t("resetPassword.passwordTooShort") || t("signup.signUpError"));
        } else if (!passwordRequirements.hasNumber) {
          setError(t("signup.passwordNeedsNumber"));
        } else if (!passwordRequirements.hasSpecial) {
          setError(t("signup.passwordNeedsSpecial"));
        }
        return;
      }
      if (!formData.confirmPassword) {
        setError(t("signup.confirmPasswordRequired"));
        return;
      }
    }
    if (!acceptTerms) {
      setError(t("signup.termsRequired"));
      return;
    }
    if (!isFieldDisabled("firstName") && !trimmedFirstName) {
      const msg = t("signup.firstNameRequired");
      setFirstNameError(msg);
      setError(msg);
      return;
    }
    if (!isFieldDisabled("firstName") && !isValidName(trimmedFirstName)) {
      const msg = t("signup.invalidFirstName");
      setFirstNameError(msg);
      setError(msg);
      return;
    }
    if (!isFieldDisabled("lastName") && !trimmedLastName) {
      const msg = t("signup.lastNameRequired");
      setLastNameError(msg);
      setError(msg);
      return;
    }
    if (!isFieldDisabled("lastName") && !isValidName(trimmedLastName)) {
      const msg = t("signup.invalidLastName");
      setLastNameError(msg);
      setError(msg);
      return;
    }
    if (!isFieldDisabled("email") && !trimmedEmail) {
      const msg = t("signup.emailRequired");
      setEmailError(msg);
      setError(msg);
      return;
    }
    if (!isFieldDisabled("email") && !hasValidEmailFormat(trimmedEmail)) {
      const msg = t("signup.invalidEmail");
      setEmailError(msg);
      setError(msg);
      return;
    }
    const trimmedPhone = formData.phone.trim();
    const validatesPhone = !isFieldDisabled("phone");
    if (validatesPhone && !trimmedPhone) {
      const msg = t("signup.phoneRequired");
      setPhoneError(msg);
      setError(msg);
      return;
    }
    if (validatesPhone && !selectedCountry) {
      const msg = t("signup.phoneCountryCodeRequired");
      setPhoneCountryCodeError(msg);
      setError(msg);
      return;
    }
    const isNumeric = /^\d+$/.test(trimmedPhone);
    const expectedLength = selectedCountry?.phoneDigits;
    if (validatesPhone && !isNumeric) {
      const msg = t("signup.invalidPhoneNumeric");
      setPhoneError(msg);
      setError(msg);
      return;
    }
    if (validatesPhone && trimmedPhone.length !== expectedLength) {
      const msg = t("signup.invalidPhoneLength").replace("{digits}", String(expectedLength));
      setPhoneError(msg);
      setError(msg);
      return;
    }
    let reCaptchaToken = "";
    try {
      reCaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);
    } catch {
      setError(t("signup.tryAgainLater"));
      return;
    }
    setIsLoading(true);
    try {
      if (isSocialCompletion && onSocialSignUpComplete && socialAuthData) {
        const authProviderType = socialAuthData.provider.toUpperCase();
        const result = await onSocialSignUpComplete(
          socialAuthData.accessToken,
          trimmedEmail,
          authProviderType,
          trimmedFirstName,
          trimmedLastName,
          trimmedPhone,
          formData.phoneCountryCode,
          acceptTerms,
          reCaptchaToken
        );
        if (!result.success) {
          setError(result.error);
        }
      } else {
        const result = await onSignUp(
          trimmedEmail,
          "TICKETON" /* TICKETON */,
          formData.password,
          formData.confirmPassword,
          trimmedFirstName,
          trimmedLastName,
          trimmedPhone,
          formData.phoneCountryCode,
          acceptTerms,
          reCaptchaToken
        );
        if (!result.success) {
          setError(result.error);
        }
      }
    } catch (error2) {
      setError(t("signup.signUpError") || error2.message);
    } finally {
      setIsLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs2("div", { className: "flex w-full min-h-[100dvh] flex-col lg:h-screen lg:max-h-screen lg:min-h-0 lg:overflow-hidden lg:flex-row", children: [
    /* @__PURE__ */ jsx5("div", { className: "flex h-16 shrink-0 items-center justify-center bg-white lg:hidden", children: logoUrl && /* @__PURE__ */ jsx5(
      "img",
      {
        src: logoUrl,
        alt: "Logo",
        className: "h-8 w-auto cursor-pointer",
        onClick: onLogoClick ?? onBack
      }
    ) }),
    /* @__PURE__ */ jsxs2("div", { className: "relative h-52 w-full shrink-0 lg:hidden", children: [
      /* @__PURE__ */ jsx5("img", { src: heroImage, alt: "", className: "w-full rounded-t-3xl h-full object-cover" }),
      /* @__PURE__ */ jsx5("div", { className: "absolute inset-0" }),
      /* @__PURE__ */ jsxs2("div", { className: "absolute inset-0 flex flex-col items-center justify-center px-6 text-center", children: [
        /* @__PURE__ */ jsx5("h2", { className: "text-white text-2xl font-bold mb-1", children: t("signup.heroTitle") }),
        /* @__PURE__ */ jsx5("p", { className: "text-white text-base opacity-90", children: finalHeroSubtitle })
      ] })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "relative z-10 -mt-6 flex flex-col rounded-t-3xl bg-white lg:mt-0 lg:basis-1/2 lg:h-full lg:min-h-0 lg:overflow-hidden lg:rounded-none", children: [
      /* @__PURE__ */ jsx5("div", { className: "hidden lg:flex items-center h-12 px-12", children: logoUrl && /* @__PURE__ */ jsx5(
        "img",
        {
          src: logoUrl,
          alt: "Logo",
          className: "h-8 w-auto cursor-pointer",
          onClick: onLogoClick ?? onBack
        }
      ) }),
      /* @__PURE__ */ jsx5("div", { className: "flex flex-col items-center px-6 pb-6 pt-2 lg:flex-1 lg:min-h-0 lg:justify-center lg:pb-4 xl:px-12", children: /* @__PURE__ */ jsxs2("div", { className: "flex flex-col gap-4 w-full max-w-lg lg:max-h-full lg:min-h-0 lg:border pt-6 lg:px-6 pb-6 lg:rounded-3xl lg:shadow-component", children: [
        /* @__PURE__ */ jsx5("h1", { className: "text-base text-ticketon-lighter-blue font-bold text-center", children: isSocialCompletion ? t("signup.completeYourProfile").replaceAll("{provider}", providerName) : title ?? t("signup.createYourAccount") }),
        isSocialCompletion && /* @__PURE__ */ jsx5("p", { className: "text-sm text-gray-500 text-center -mt-2", children: t("signup.completeProfileDescription").replaceAll("{provider}", providerName) }),
        /* @__PURE__ */ jsxs2("form", { className: "space-y-3 lg:min-h-0 lg:overflow-y-auto lg:-mx-3 lg:px-3 lg:py-2", onSubmit: handleSignUp, noValidate: true, children: [
          /* @__PURE__ */ jsxs2("div", { className: "grid grid-cols-2 gap-3", children: [
            /* @__PURE__ */ jsxs2("div", { children: [
              /* @__PURE__ */ jsx5(Label2, { htmlFor: "firstName", className: "sr-only", children: t("signup.firstName") }),
              /* @__PURE__ */ jsx5(
                Input2,
                {
                  id: "firstName",
                  type: "text",
                  placeholder: t("signup.firstNamePlaceholder"),
                  value: formData.firstName,
                  onChange: handleInputChange("firstName"),
                  className: firstNameError ? inputStyleError : inputStyle,
                  autoComplete: "given-name",
                  disabled: isLoading || isFieldDisabled("firstName")
                }
              )
            ] }),
            /* @__PURE__ */ jsxs2("div", { children: [
              /* @__PURE__ */ jsx5(Label2, { htmlFor: "lastName", className: "sr-only", children: t("signup.lastName") }),
              /* @__PURE__ */ jsx5(
                Input2,
                {
                  id: "lastName",
                  type: "text",
                  placeholder: t("signup.lastNamePlaceholder"),
                  value: formData.lastName,
                  onChange: handleInputChange("lastName"),
                  className: lastNameError ? inputStyleError : inputStyle,
                  autoComplete: "family-name",
                  disabled: isLoading || isFieldDisabled("lastName")
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs2("div", { children: [
            /* @__PURE__ */ jsx5(Label2, { htmlFor: "email", className: "sr-only", children: t("signup.email") }),
            /* @__PURE__ */ jsx5(
              Input2,
              {
                id: "email",
                type: "email",
                placeholder: t("signup.emailPlaceholder"),
                value: formData.email,
                onChange: handleInputChange("email"),
                className: `${emailError ? inputStyleError : inputStyle} ${isSocialCompletion ? "bg-gray-100" : ""}`,
                autoComplete: "username",
                disabled: isLoading || isSocialCompletion || isFieldDisabled("email"),
                readOnly: isSocialCompletion
              }
            )
          ] }),
          /* @__PURE__ */ jsxs2("div", { className: "grid grid-cols-2 gap-3", children: [
            /* @__PURE__ */ jsxs2("div", { children: [
              /* @__PURE__ */ jsx5(Label2, { htmlFor: "phoneCountryCode", className: "sr-only", children: t("signup.phoneCountryCode") }),
              /* @__PURE__ */ jsxs2(
                Select,
                {
                  value: formData.phoneCountryCode,
                  onValueChange: handleCountryChange,
                  disabled: isLoading || isFieldDisabled("phone"),
                  children: [
                    /* @__PURE__ */ jsx5(
                      SelectTrigger,
                      {
                        id: "phoneCountryCode",
                        variant: "outlined",
                        size: "default",
                        className: "w-full h-[60px] rounded-2xl",
                        "aria-label": t("signup.phoneCountryCode"),
                        state: phoneCountryCodeError ? SELECT_STATES.ERROR : SELECT_STATES.NORMAL,
                        errorMessage: phoneCountryCodeError,
                        children: /* @__PURE__ */ jsx5(
                          SelectValue,
                          {
                            placeholder: t("signup.phoneCountryCodePlaceholder"),
                            children: selectedCountry ? /* @__PURE__ */ jsxs2("span", { className: "inline-flex items-center gap-2 min-w-0 w-full", children: [
                              /* @__PURE__ */ jsx5("span", { className: `fi fi-${selectedCountry.iso2} fis flex-shrink-0`, style: { width: 24, height: 18 } }),
                              /* @__PURE__ */ jsx5("span", { className: "truncate", children: `+${selectedCountry.code}` })
                            ] }) : t("signup.phoneCountryCodePlaceholder")
                          }
                        )
                      }
                    ),
                    /* @__PURE__ */ jsx5(SelectContent, { children: COUNTRIES_PHONE_CODES.map((country) => /* @__PURE__ */ jsx5(
                      SelectItem,
                      {
                        value: country.abbreviation,
                        children: /* @__PURE__ */ jsxs2("span", { className: "inline-flex items-center gap-2", children: [
                          /* @__PURE__ */ jsx5("span", { className: `fi fi-${country.iso2} fis flex-shrink-0`, style: { width: 24, height: 18 } }),
                          /* @__PURE__ */ jsx5("span", { className: "hidden sm:inline", children: t(`countries.${country.iso2}`) }),
                          /* @__PURE__ */ jsx5("span", { children: `(+${country.code})` })
                        ] })
                      },
                      country.abbreviation
                    )) })
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs2("div", { children: [
              /* @__PURE__ */ jsx5(Label2, { htmlFor: "phone", className: "sr-only", children: t("signup.phone") }),
              /* @__PURE__ */ jsx5(
                Input2,
                {
                  id: "phone",
                  type: "tel",
                  placeholder: t("signup.phonePlaceholder"),
                  value: formData.phone,
                  onChange: handleInputChange("phone"),
                  className: phoneError ? inputStyleError : inputStyle,
                  autoComplete: "tel",
                  disabled: isLoading || isFieldDisabled("phone")
                }
              )
            ] })
          ] }),
          extraFields,
          requiresPassword && /* @__PURE__ */ jsxs2(Fragment2, { children: [
            /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
              /* @__PURE__ */ jsx5(Label2, { htmlFor: "password", className: "sr-only", children: t("signup.password") }),
              /* @__PURE__ */ jsx5(
                Input2,
                {
                  id: "password",
                  type: showPassword ? "text" : "password",
                  placeholder: t("signup.passwordPlaceholder"),
                  value: formData.password,
                  onChange: handleInputChange("password"),
                  className: inputStyle + " pr-12",
                  autoComplete: "new-password",
                  disabled: isLoading
                }
              ),
              /* @__PURE__ */ jsx5(
                "button",
                {
                  type: "button",
                  onClick: () => setShowPassword(!showPassword),
                  className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700",
                  children: showPassword ? /* @__PURE__ */ jsx5(FaEyeSlash2, { size: 16 }) : /* @__PURE__ */ jsx5(FaEye2, { size: 16 })
                }
              )
            ] }),
            formData.password && /* @__PURE__ */ jsx5("div", { className: "mt-1", children: /* @__PURE__ */ jsx5(PasswordStrengthMeter, { password: formData.password, t }) }),
            /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
              /* @__PURE__ */ jsx5(Label2, { htmlFor: "confirmPassword", className: "sr-only", children: t("signup.confirmPassword") }),
              /* @__PURE__ */ jsx5(
                Input2,
                {
                  id: "confirmPassword",
                  type: showConfirmPassword ? "text" : "password",
                  placeholder: t("signup.confirmPasswordPlaceholder"),
                  value: formData.confirmPassword,
                  onChange: handleInputChange("confirmPassword"),
                  className: inputStyle + " pr-12",
                  autoComplete: "new-password",
                  disabled: isLoading
                }
              ),
              /* @__PURE__ */ jsx5(
                "button",
                {
                  type: "button",
                  onClick: () => setShowConfirmPassword(!showConfirmPassword),
                  className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700",
                  children: showConfirmPassword ? /* @__PURE__ */ jsx5(FaEyeSlash2, { size: 16 }) : /* @__PURE__ */ jsx5(FaEye2, { size: 16 })
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs2("div", { className: "flex items-start space-x-3", children: [
            /* @__PURE__ */ jsx5(
              Checkbox,
              {
                id: "terms",
                checked: acceptTerms,
                onCheckedChange: (checked) => setAcceptTerms(checked),
                className: "mt-1",
                disabled: isLoading
              }
            ),
            /* @__PURE__ */ jsxs2(
              Label2,
              {
                htmlFor: "terms",
                className: "text-sm text-gray-600 leading-relaxed",
                children: [
                  t("signup.acceptTerms"),
                  " ",
                  /* @__PURE__ */ jsx5(
                    "a",
                    {
                      href: termsUrl || TERMS_URL2,
                      className: "text-ticketon-secondary-blue hover:underline font-semibold",
                      target: "_blank",
                      rel: "noopener noreferrer",
                      children: t("signup.termsOfService")
                    }
                  )
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsx5(
            Button2,
            {
              className: "w-full h-11 text-white font-medium hover:opacity-90 disabled:opacity-50 rounded-lg bg-ticketon-lighter-blue",
              type: "submit",
              disabled: isLoading,
              children: isLoading ? isSocialCompletion ? t("signup.finishingAccount") : t("signup.creatingAccount") : isSocialCompletion ? t("signup.finishCreatingAccount").replaceAll("{provider}", providerName) : submitLabel ?? t("signup.createAccount")
            }
          )
        ] }),
        error && /* @__PURE__ */ jsx5("div", { className: "bg-red-50 border border-red-200 rounded-md p-3", children: /* @__PURE__ */ jsx5("p", { className: "text-red-600 text-sm text-center", children: error }) }),
        !isSocialCompletion && /* @__PURE__ */ jsxs2("p", { className: "text-center text-gray-600 text-sm", children: [
          t("signup.alreadyHaveAccount"),
          " ",
          /* @__PURE__ */ jsx5(
            "button",
            {
              onClick: onLoginClick,
              className: "hover:underline font-medium text-ticketon-secondary-blue",
              children: t("signup.signIn")
            }
          )
        ] }),
        /* @__PURE__ */ jsxs2("p", { className: "text-center text-gray-400 text-xs leading-relaxed", children: [
          t("signup.disclaimerPrefix"),
          " ",
          /* @__PURE__ */ jsx5(
            "a",
            {
              href: termsUrl || TERMS_URL2,
              target: "_blank",
              rel: "noopener noreferrer",
              className: "underline hover:text-gray-600 cursor-pointer",
              children: t("signup.termsOfUse")
            }
          ),
          " ",
          t("signup.disclaimerMiddle"),
          " ",
          /* @__PURE__ */ jsx5(
            "a",
            {
              href: privacyUrl || PRIVACY_URL2,
              target: "_blank",
              rel: "noopener noreferrer",
              className: "underline hover:text-gray-600 cursor-pointer",
              children: t("signup.privacyPolicy")
            }
          )
        ] })
      ] }) })
    ] }),
    /* @__PURE__ */ jsx5("div", { className: "hidden lg:flex lg:basis-1/2 lg:min-h-0 lg:h-full relative p-6 bg-white", children: /* @__PURE__ */ jsxs2("div", { className: "relative w-full h-full min-h-0 rounded-3xl overflow-hidden bg-black", children: [
      /* @__PURE__ */ jsx5(
        "img",
        {
          src: heroImage,
          alt: "",
          className: "absolute inset-0 h-full w-full object-contain object-center"
        }
      ),
      /* @__PURE__ */ jsx5("div", { className: "absolute bottom-12 left-0 right-0 flex justify-center p-10", children: /* @__PURE__ */ jsxs2("div", { className: "w-full max-w-[470px] text-center", children: [
        /* @__PURE__ */ jsx5("h2", { className: "text-white text-5xl font-bold mb-2", children: t("signup.heroTitle") }),
        /* @__PURE__ */ jsx5("p", { className: "text-white text-2xl opacity-90", children: finalHeroSubtitle })
      ] }) })
    ] }) })
  ] });
}

// src/pages/ForgotPasswordPage.tsx
import { useState as useState5 } from "react";
import { Button as Button3, Input as Input3, Label as Label3, cn } from "ui";
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
function ForgotPasswordPage({
  onSendResetEmail,
  onBack,
  onLoginClick,
  logoUrl,
  t,
  className
}) {
  const [email, setEmail] = useState5("");
  const [error, setError] = useState5();
  const [success, setSuccess] = useState5(false);
  const [isLoading, setIsLoading] = useState5(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(void 0);
    setSuccess(false);
    setIsLoading(true);
    try {
      const result = await onSendResetEmail(email);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error);
      }
    } catch (error2) {
      setError(t("forgotPassword.sendEmailError") || error2.message);
    } finally {
      setIsLoading(false);
    }
  };
  if (success) {
    return /* @__PURE__ */ jsx6("div", { className: cn("min-h-screen w-full flex items-center justify-center bg-gray-100 px-4 sm:px-6 md:px-8 lg:px-0", className), children: /* @__PURE__ */ jsx6("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs3("div", { className: "bg-white rounded-2xl shadow-xl p-8 text-center", children: [
      /* @__PURE__ */ jsx6("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx6(
        "img",
        {
          src: logoUrl,
          alt: "Logo",
          className: "h-12 w-auto mx-auto hover:cursor-pointer",
          onClick: onBack,
          loading: "eager",
          decoding: "async"
        }
      ) }),
      /* @__PURE__ */ jsxs3("div", { className: "mb-8", children: [
        /* @__PURE__ */ jsx6("div", { className: "w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4", children: /* @__PURE__ */ jsx6(
          "svg",
          {
            className: "w-8 h-8 text-green-600",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24",
            children: /* @__PURE__ */ jsx6(
              "path",
              {
                strokeLinecap: "round",
                strokeLinejoin: "round",
                strokeWidth: 2,
                d: "M5 13l4 4L19 7"
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsx6("h1", { className: "text-2xl font-bold text-gray-900 mb-2", children: t("forgotPassword.emailSent") }),
        /* @__PURE__ */ jsx6("p", { className: "text-gray-600 leading-relaxed", children: t("forgotPassword.checkYourEmail") })
      ] }),
      /* @__PURE__ */ jsx6("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6", children: /* @__PURE__ */ jsx6("p", { className: "text-blue-800 text-sm", children: t("forgotPassword.emailInstructions") }) }),
      /* @__PURE__ */ jsx6(
        Button3,
        {
          onClick: onLoginClick,
          className: "w-full h-11 text-white font-medium hover:opacity-90 rounded-lg bg-primary",
          children: t("forgotPassword.backToLogin")
        }
      )
    ] }) }) });
  }
  return /* @__PURE__ */ jsx6("div", { className: cn("min-h-screen w-full flex items-center justify-center bg-gray-100 px-4 sm:px-6 md:px-8 lg:px-0", className), children: /* @__PURE__ */ jsx6("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs3("div", { className: "bg-white rounded-2xl shadow-xl p-8", children: [
    /* @__PURE__ */ jsx6("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx6(
      "img",
      {
        src: logoUrl,
        alt: "Logo",
        className: "h-12 w-auto mx-auto hover:cursor-pointer",
        onClick: onBack,
        loading: "eager",
        decoding: "async"
      }
    ) }),
    /* @__PURE__ */ jsxs3("div", { className: "text-center mb-8", children: [
      /* @__PURE__ */ jsx6("h1", { className: "text-2xl font-bold text-gray-900 mb-2", children: t("forgotPassword.forgotPassword") }),
      /* @__PURE__ */ jsx6("p", { className: "text-gray-600 leading-relaxed", children: t("forgotPassword.enterEmailToReset") })
    ] }),
    /* @__PURE__ */ jsxs3("form", { className: "space-y-4", onSubmit: handleSubmit, noValidate: true, children: [
      /* @__PURE__ */ jsxs3("div", { children: [
        /* @__PURE__ */ jsx6(Label3, { htmlFor: "email", className: "sr-only", children: t("forgotPassword.email") }),
        /* @__PURE__ */ jsx6(
          Input3,
          {
            id: "email",
            type: "email",
            placeholder: t("forgotPassword.emailPlaceholder"),
            value: email,
            onChange: (e) => setEmail(e.target.value),
            className: "w-full h-12 bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 rounded-lg",
            autoComplete: "email",
            disabled: isLoading
          }
        )
      ] }),
      /* @__PURE__ */ jsx6(
        Button3,
        {
          className: "w-full h-11 text-white font-medium hover:opacity-90 disabled:opacity-50 rounded-lg bg-primary",
          type: "submit",
          disabled: isLoading,
          children: isLoading ? t("forgotPassword.sendingEmail") : t("forgotPassword.sendResetEmail")
        }
      )
    ] }),
    error && /* @__PURE__ */ jsx6("div", { className: "bg-red-50 border border-red-200 rounded-md p-3 mt-4", children: /* @__PURE__ */ jsx6("p", { className: "text-red-600 text-sm text-center", children: error }) }),
    /* @__PURE__ */ jsxs3("p", { className: "text-center text-gray-600 text-sm mt-6", children: [
      t("forgotPassword.rememberPassword"),
      " ",
      /* @__PURE__ */ jsx6(
        "button",
        {
          onClick: onLoginClick,
          className: "hover:underline font-medium text-ticketon-secondary-blue",
          children: t("forgotPassword.backToLogin")
        }
      )
    ] })
  ] }) }) });
}

// src/pages/ResetPasswordPage.tsx
import { useState as useState6 } from "react";
import { Button as Button4, Input as Input4, Label as Label4, PasswordStrengthMeter as PasswordStrengthMeter2, cn as cn2 } from "ui";
import { FaEye as FaEye3, FaEyeSlash as FaEyeSlash3 } from "react-icons/fa";
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
function ResetPasswordPage({
  onResetPassword,
  onBack,
  onLoginClick,
  logoUrl,
  email,
  code,
  t,
  className
}) {
  const [formData, setFormData] = useState6({
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState6(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState6(false);
  const [error, setError] = useState6();
  const [success, setSuccess] = useState6(false);
  const [isLoading, setIsLoading] = useState6(false);
  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(void 0);
    setSuccess(false);
    setIsLoading(true);
    if (formData.password !== formData.confirmPassword) {
      setError(t("resetPassword.passwordMismatch"));
      setIsLoading(false);
      return;
    }
    if (formData.password.length < 8) {
      setError(t("resetPassword.passwordTooShort"));
      setIsLoading(false);
      return;
    }
    try {
      const result = await onResetPassword(
        email || "",
        "TICKETON" /* TICKETON */,
        formData.password,
        formData.confirmPassword,
        code || ""
      );
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error);
      }
    } catch (error2) {
      setError(t("resetPassword.resetError") || error2.message);
    } finally {
      setIsLoading(false);
    }
  };
  if (success) {
    return /* @__PURE__ */ jsx7("div", { className: cn2("min-h-screen w-full flex items-center justify-center bg-gray-100 px-4 sm:px-6 md:px-8 lg:px-0", className), children: /* @__PURE__ */ jsx7("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs4("div", { className: "bg-white rounded-2xl shadow-xl p-8 text-center", children: [
      /* @__PURE__ */ jsx7("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx7(
        "img",
        {
          src: logoUrl,
          alt: "Logo",
          className: "h-12 w-auto mx-auto hover:cursor-pointer",
          onClick: onBack,
          loading: "eager",
          decoding: "async"
        }
      ) }),
      /* @__PURE__ */ jsxs4("div", { className: "mb-8", children: [
        /* @__PURE__ */ jsx7("div", { className: "w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4", children: /* @__PURE__ */ jsx7(
          "svg",
          {
            className: "w-8 h-8 text-green-600",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24",
            children: /* @__PURE__ */ jsx7(
              "path",
              {
                strokeLinecap: "round",
                strokeLinejoin: "round",
                strokeWidth: 2,
                d: "M5 13l4 4L19 7"
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsx7("h1", { className: "text-2xl font-bold text-gray-900 mb-2", children: t("resetPassword.passwordResetSuccess") }),
        /* @__PURE__ */ jsx7("p", { className: "text-gray-600 leading-relaxed", children: t("resetPassword.passwordUpdated") })
      ] }),
      /* @__PURE__ */ jsx7("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 mb-6", children: /* @__PURE__ */ jsx7("p", { className: "text-green-800 text-sm", children: t("resetPassword.youCanNowLogin") }) }),
      /* @__PURE__ */ jsx7(
        Button4,
        {
          onClick: onLoginClick,
          className: "w-full h-11 text-white font-medium hover:opacity-90 rounded-lg bg-primary",
          children: t("resetPassword.continueToLogin")
        }
      )
    ] }) }) });
  }
  return /* @__PURE__ */ jsx7("div", { className: cn2("min-h-screen w-full flex items-center justify-center bg-gray-100 px-4 sm:px-6 md:px-8 lg:px-0", className), children: /* @__PURE__ */ jsx7("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs4("div", { className: "bg-white rounded-2xl shadow-xl p-8", children: [
    /* @__PURE__ */ jsx7("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx7(
      "img",
      {
        src: logoUrl,
        alt: "Logo",
        className: "h-12 w-auto mx-auto hover:cursor-pointer",
        onClick: onBack,
        loading: "eager",
        decoding: "async"
      }
    ) }),
    /* @__PURE__ */ jsxs4("div", { className: "text-center mb-8", children: [
      /* @__PURE__ */ jsx7("h1", { className: "text-2xl font-bold text-gray-900 mb-2", children: t("resetPassword.resetPassword") }),
      /* @__PURE__ */ jsx7("p", { className: "text-gray-600 leading-relaxed", children: t("resetPassword.enterNewPassword") })
    ] }),
    /* @__PURE__ */ jsxs4("form", { className: "space-y-4", onSubmit: handleSubmit, noValidate: true, children: [
      /* @__PURE__ */ jsxs4("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx7(
          Label4,
          {
            htmlFor: "email",
            className: "block text-sm font-medium text-gray-700",
            children: t("resetPassword.email")
          }
        ),
        /* @__PURE__ */ jsx7(
          Input4,
          {
            id: "email",
            type: "email",
            value: email,
            disabled: true,
            className: "w-full h-11 bg-gray-50 border-gray-200 text-gray-900 cursor-not-allowed"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs4("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx7(
          Label4,
          {
            htmlFor: "password",
            className: "block text-sm font-medium text-gray-700",
            children: t("resetPassword.newPassword")
          }
        ),
        /* @__PURE__ */ jsxs4("div", { className: "relative", children: [
          /* @__PURE__ */ jsx7(
            Input4,
            {
              id: "password",
              type: showPassword ? "text" : "password",
              placeholder: t("resetPassword.newPasswordPlaceholder"),
              value: formData.password,
              onChange: handleInputChange("password"),
              className: "w-full h-12 bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 rounded-lg pr-12",
              autoComplete: "new-password",
              required: true,
              disabled: isLoading
            }
          ),
          /* @__PURE__ */ jsx7(
            "button",
            {
              type: "button",
              onClick: () => setShowPassword(!showPassword),
              className: "absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700",
              children: showPassword ? /* @__PURE__ */ jsx7(FaEyeSlash3, { size: 16 }) : /* @__PURE__ */ jsx7(FaEye3, { size: 16 })
            }
          )
        ] })
      ] }),
      formData.password && /* @__PURE__ */ jsx7("div", { className: "mt-2", children: /* @__PURE__ */ jsx7(PasswordStrengthMeter2, { password: formData.password, t }) }),
      /* @__PURE__ */ jsxs4("div", { className: "relative", children: [
        /* @__PURE__ */ jsx7(Label4, { htmlFor: "confirmPassword", className: "sr-only", children: t("resetPassword.confirmNewPassword") }),
        /* @__PURE__ */ jsx7(
          Input4,
          {
            id: "confirmPassword",
            type: showConfirmPassword ? "text" : "password",
            placeholder: t("resetPassword.confirmNewPasswordPlaceholder"),
            value: formData.confirmPassword,
            onChange: handleInputChange("confirmPassword"),
            className: "w-full h-12 bg-white border-gray-200 text-gray-900 placeholder:text-gray-500 rounded-lg pr-12",
            autoComplete: "new-password",
            disabled: isLoading
          }
        ),
        /* @__PURE__ */ jsx7(
          "button",
          {
            type: "button",
            onClick: () => setShowConfirmPassword(!showConfirmPassword),
            className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700",
            children: showConfirmPassword ? /* @__PURE__ */ jsx7(FaEyeSlash3, { size: 16 }) : /* @__PURE__ */ jsx7(FaEye3, { size: 16 })
          }
        )
      ] }),
      /* @__PURE__ */ jsx7(
        Button4,
        {
          className: "w-full h-11 text-white font-medium hover:opacity-90 disabled:opacity-50 rounded-lg bg-primary",
          type: "submit",
          disabled: isLoading,
          children: isLoading ? t("resetPassword.updatingPassword") : t("resetPassword.updatePassword")
        }
      )
    ] }),
    error && /* @__PURE__ */ jsx7("div", { className: "bg-red-50 border border-red-200 rounded-md p-3 mt-4", children: /* @__PURE__ */ jsx7("p", { className: "text-red-600 text-sm text-center", children: error }) }),
    /* @__PURE__ */ jsxs4("p", { className: "text-center text-gray-600 text-sm mt-6", children: [
      t("resetPassword.rememberPassword"),
      " ",
      /* @__PURE__ */ jsx7(
        "button",
        {
          onClick: onLoginClick,
          className: "hover:underline font-medium text-ticketon-secondary-blue",
          children: t("resetPassword.backToLogin")
        }
      )
    ] })
  ] }) }) });
}

// src/pages/OTPVerificationPage.tsx
import { useState as useState7, useRef as useRef3, useEffect as useEffect5 } from "react";
import { Button as Button5, Input as Input5, Label as Label5, cn as cn3 } from "ui";
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
function OTPVerificationPage({
  onVerifyOTP,
  onResendOTP,
  onBack,
  onLoginClick,
  logoUrl,
  email,
  t,
  className
}) {
  const [otp, setOtp] = useState7(["", "", "", "", "", ""]);
  const [error, setError] = useState7();
  const [isLoading, setIsLoading] = useState7(false);
  const [isResending, setIsResending] = useState7(false);
  const [resendCountdown, setResendCountdown] = useState7(0);
  const inputRefs = useRef3([]);
  useEffect5(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(
        () => setResendCountdown(resendCountdown - 1),
        1e3
      );
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(void 0);
    setIsLoading(true);
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError(t("otpVerification.incompleteCode"));
      setIsLoading(false);
      return;
    }
    try {
      const result = await onVerifyOTP(otpString);
      if (!result.success) {
        setError(result.error);
      }
    } catch (error2) {
      setError(
        t("otpVerification.verificationError") || error2.message
      );
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendOTP = async () => {
    setError(void 0);
    setIsResending(true);
    try {
      const result = await onResendOTP();
      if (result.success) {
        setResendCountdown(60);
      } else {
        setError(result.error);
      }
    } catch (error2) {
      setError(t("otpVerification.resendError") || error2.message);
    } finally {
      setIsResending(false);
    }
  };
  return /* @__PURE__ */ jsx8("div", { className: cn3("min-h-screen w-full flex items-center justify-center bg-gray-100 px-4 sm:px-6 md:px-8 lg:px-0", className), children: /* @__PURE__ */ jsx8("div", { className: "w-full max-w-md", children: /* @__PURE__ */ jsxs5("div", { className: "bg-white rounded-2xl shadow-xl p-8", children: [
    /* @__PURE__ */ jsx8("div", { className: "text-center mb-8", children: /* @__PURE__ */ jsx8(
      "img",
      {
        src: logoUrl,
        alt: "Logo",
        className: "h-12 w-auto mx-auto hover:cursor-pointer",
        onClick: onBack,
        loading: "eager",
        decoding: "async"
      }
    ) }),
    /* @__PURE__ */ jsxs5("div", { className: "text-center mb-8", children: [
      /* @__PURE__ */ jsx8("h1", { className: "text-2xl font-bold text-gray-900 mb-2", children: t("otpVerification.verifyCode") }),
      /* @__PURE__ */ jsx8("p", { className: "text-gray-600 leading-relaxed", children: t("otpVerification.enterCodeSent") }),
      email && /* @__PURE__ */ jsx8("p", { className: "text-sm text-gray-500 mt-2", children: email })
    ] }),
    /* @__PURE__ */ jsxs5("form", { className: "space-y-6", onSubmit: handleSubmit, children: [
      /* @__PURE__ */ jsxs5("div", { children: [
        /* @__PURE__ */ jsx8(Label5, { className: "sr-only", children: t("otpVerification.verificationCode") }),
        /* @__PURE__ */ jsx8("div", { className: "flex gap-3 justify-center", children: otp.map((digit, index) => /* @__PURE__ */ jsx8(
          Input5,
          {
            ref: (el) => {
              inputRefs.current[index] = el;
            },
            type: "text",
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 1,
            value: digit,
            onChange: (e) => handleOtpChange(index, e.target.value),
            onKeyDown: (e) => handleKeyDown(index, e),
            className: "w-12 h-12 text-center text-lg font-semibold bg-white border-gray-200 text-gray-900 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20",
            disabled: isLoading
          },
          index
        )) })
      ] }),
      /* @__PURE__ */ jsx8(
        Button5,
        {
          className: "w-full h-11 text-white font-medium hover:opacity-90 disabled:opacity-50 rounded-lg bg-primary",
          type: "submit",
          disabled: isLoading || otp.join("").length !== 6,
          children: isLoading ? t("otpVerification.verifying") : t("otpVerification.verifyCode")
        }
      )
    ] }),
    error && /* @__PURE__ */ jsx8("div", { className: "bg-red-50 border border-red-200 rounded-md p-3 mt-4", children: /* @__PURE__ */ jsx8("p", { className: "text-red-600 text-sm text-center", children: error }) }),
    /* @__PURE__ */ jsxs5("div", { className: "text-center mt-6", children: [
      /* @__PURE__ */ jsx8("p", { className: "text-gray-600 text-sm mb-4", children: t("otpVerification.didntReceiveCode") }),
      /* @__PURE__ */ jsx8(
        "button",
        {
          onClick: handleResendOTP,
          disabled: isResending || resendCountdown > 0,
          className: "text-sm font-medium text-ticketon-secondary-blue hover:underline disabled:opacity-50 disabled:cursor-not-allowed",
          children: isResending ? t("otpVerification.sending") : resendCountdown > 0 ? `${t("otpVerification.resendIn")} ${resendCountdown}s` : t("otpVerification.resendCode")
        }
      )
    ] }),
    /* @__PURE__ */ jsxs5("p", { className: "text-center text-gray-600 text-sm mt-6", children: [
      t("otpVerification.wrongEmail"),
      " ",
      /* @__PURE__ */ jsx8(
        "button",
        {
          onClick: onLoginClick,
          className: "hover:underline font-medium text-ticketon-secondary-blue",
          children: t("otpVerification.backToLogin")
        }
      )
    ] })
  ] }) }) });
}

// src/pages/AccountLinkingPage.tsx
import { useState as useState8 } from "react";
import { Button as Button6 } from "ui";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook, FaApple } from "react-icons/fa";
import { jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
function AccountLinkingPage({
  accountLinkingData,
  onConfirmLinking,
  onLoginWithEmail,
  onBack,
  logoUrl,
  t,
  heroImage = "/images/join-ticketon-login.webp"
}) {
  const [isLoading, setIsLoading] = useState8(false);
  const [error, setError] = useState8();
  const providerName = accountLinkingData.provider.charAt(0).toUpperCase() + accountLinkingData.provider.slice(1).toLowerCase();
  const getProviderIcon = () => {
    switch (accountLinkingData.provider.toLowerCase()) {
      case "google":
        return /* @__PURE__ */ jsx9(FcGoogle, { className: "w-6 h-6" });
      case "facebook":
        return /* @__PURE__ */ jsx9(FaFacebook, { className: "w-6 h-6 text-blue-600" });
      case "apple":
        return /* @__PURE__ */ jsx9(FaApple, { className: "w-6 h-6" });
      default:
        return null;
    }
  };
  const handleConfirmLinking = async () => {
    setIsLoading(true);
    setError(void 0);
    try {
      const result = await onConfirmLinking(
        accountLinkingData.accessToken,
        accountLinkingData.email,
        accountLinkingData.provider
      );
      if (!result.success) {
        setError(result.error || t("accountLinking.linkingError").replaceAll("{provider}", providerName));
      }
    } catch {
      setError(t("accountLinking.linkingError").replaceAll("{provider}", providerName));
    } finally {
      setIsLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs6("div", { className: "flex w-full min-h-[100dvh] flex-col lg:h-screen lg:max-h-screen lg:min-h-0 lg:overflow-hidden lg:flex-row", children: [
    /* @__PURE__ */ jsx9("div", { className: "flex h-16 shrink-0 items-center justify-center bg-white lg:hidden", children: logoUrl && /* @__PURE__ */ jsx9(
      "img",
      {
        src: logoUrl,
        alt: "Logo",
        className: "h-10 w-auto cursor-pointer",
        onClick: onBack
      }
    ) }),
    /* @__PURE__ */ jsxs6("div", { className: "relative h-52 w-full shrink-0 lg:hidden", children: [
      /* @__PURE__ */ jsx9("img", { src: heroImage, alt: "", className: "w-full rounded-t-3xl h-full object-cover" }),
      /* @__PURE__ */ jsxs6("div", { className: "absolute inset-0 flex flex-col items-center justify-center px-6 text-center", children: [
        /* @__PURE__ */ jsx9("h2", { className: "text-white text-2xl font-bold mb-1", children: t("accountLinking.heroTitle") }),
        /* @__PURE__ */ jsx9("p", { className: "text-white text-base opacity-90", children: t("accountLinking.heroSubtitle").replaceAll("{provider}", providerName) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs6("div", { className: "relative z-10 -mt-6 flex flex-col rounded-t-3xl bg-white lg:mt-0 lg:basis-1/2 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:rounded-none", children: [
      /* @__PURE__ */ jsx9("div", { className: "hidden lg:flex items-center h-16 px-12", children: logoUrl && /* @__PURE__ */ jsx9(
        "img",
        {
          src: logoUrl,
          alt: "Logo",
          className: "h-10 w-auto cursor-pointer",
          onClick: onBack
        }
      ) }),
      /* @__PURE__ */ jsx9("div", { className: "flex flex-col px-6 pb-6 pt-2 lg:flex-1 lg:items-center lg:justify-center lg:pb-8 xl:px-12", children: /* @__PURE__ */ jsxs6("div", { className: "flex flex-col gap-6 w-full max-w-lg lg:border pt-8 lg:px-8 pb-8 lg:rounded-3xl lg:shadow-component", children: [
        /* @__PURE__ */ jsx9("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx9("div", { className: "w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center", children: getProviderIcon() }) }),
        /* @__PURE__ */ jsxs6("div", { className: "text-center", children: [
          /* @__PURE__ */ jsx9("h1", { className: "text-xl font-bold text-gray-900 mb-2", children: t("accountLinking.accountFound") }),
          /* @__PURE__ */ jsx9("p", { className: "text-gray-600 text-sm", children: t("accountLinking.existingAccountMessage").replaceAll("{provider}", providerName) })
        ] }),
        /* @__PURE__ */ jsxs6("div", { className: "bg-gray-50 rounded-xl p-4 text-center", children: [
          /* @__PURE__ */ jsx9("p", { className: "text-sm text-gray-500 mb-1", children: t("accountLinking.email") }),
          /* @__PURE__ */ jsx9("p", { className: "font-medium text-gray-900", children: accountLinkingData.email })
        ] }),
        /* @__PURE__ */ jsx9("div", { className: "bg-blue-50 border border-blue-100 rounded-xl p-4", children: /* @__PURE__ */ jsx9("p", { className: "text-sm text-blue-800", children: t("accountLinking.linkingExplanation").replaceAll("{provider}", providerName) }) }),
        error && /* @__PURE__ */ jsx9("div", { className: "bg-red-50 border border-red-200 rounded-md p-3", children: /* @__PURE__ */ jsx9("p", { className: "text-red-600 text-sm text-center", children: error }) }),
        /* @__PURE__ */ jsxs6("div", { className: "space-y-3", children: [
          /* @__PURE__ */ jsxs6(
            Button6,
            {
              className: "w-full h-12 text-white font-medium hover:opacity-90 disabled:opacity-50 rounded-lg bg-ticketon-lighter-blue flex items-center justify-center gap-2",
              onClick: handleConfirmLinking,
              disabled: isLoading,
              children: [
                getProviderIcon(),
                isLoading ? t("accountLinking.linking").replaceAll("{provider}", providerName) : t("accountLinking.continueWithProvider").replaceAll("{provider}", providerName)
              ]
            }
          ),
          /* @__PURE__ */ jsxs6("div", { className: "relative flex items-center justify-center", children: [
            /* @__PURE__ */ jsx9("div", { className: "absolute inset-0 flex items-center", children: /* @__PURE__ */ jsx9("div", { className: "w-full border-t border-gray-200" }) }),
            /* @__PURE__ */ jsx9("span", { className: "relative bg-white px-4 text-sm text-gray-500", children: t("login.or") })
          ] }),
          /* @__PURE__ */ jsx9(
            Button6,
            {
              variant: "outline",
              className: "w-full h-12 font-medium rounded-lg border-gray-300 text-gray-700 hover:bg-gray-50",
              onClick: onLoginWithEmail,
              disabled: isLoading,
              children: t("accountLinking.loginWithEmail")
            }
          )
        ] }),
        /* @__PURE__ */ jsx9("p", { className: "text-center text-gray-400 text-xs leading-relaxed", children: t("accountLinking.securityNote").replaceAll("{provider}", providerName) })
      ] }) })
    ] }),
    /* @__PURE__ */ jsx9("div", { className: "hidden lg:flex lg:basis-1/2 lg:min-h-0 lg:h-full relative p-6 bg-white", children: /* @__PURE__ */ jsxs6("div", { className: "relative w-full h-full min-h-0 rounded-3xl overflow-hidden bg-black", children: [
      /* @__PURE__ */ jsx9(
        "img",
        {
          src: heroImage,
          alt: "",
          className: "absolute inset-0 h-full w-full object-contain object-center"
        }
      ),
      /* @__PURE__ */ jsx9("div", { className: "absolute bottom-12 left-0 right-0 flex justify-center p-10", children: /* @__PURE__ */ jsxs6("div", { className: "w-full max-w-[470px] text-center", children: [
        /* @__PURE__ */ jsx9("h2", { className: "text-white text-5xl font-bold mb-2", children: t("accountLinking.heroTitle") }),
        /* @__PURE__ */ jsx9("p", { className: "text-white text-2xl opacity-90", children: t("accountLinking.heroSubtitle").replaceAll("{provider}", providerName) })
      ] }) })
    ] }) })
  ] });
}

// src/pages/AuthRouter.tsx
import { useEffect as useEffect6 } from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
function AuthRouter({
  route,
  logoUrl,
  heroImage,
  showHeroText,
  urlEmail,
  code,
  showSignUpLink = true,
  initialError,
  socialAuthData,
  accountLinkingData,
  ...config
}) {
  const auth = useAuthHandlers(config);
  useEffect6(() => {
    auth.setRoute(route);
  }, [route]);
  useEffect6(() => {
    if (urlEmail) {
      auth.setEmail(urlEmail);
    }
  }, [urlEmail]);
  const email = urlEmail || auth.email;
  switch (route) {
    case "signup":
      return /* @__PURE__ */ jsx10(
        SignupPage,
        {
          onSignUp: auth.handleSignUp,
          onSocialSignUpComplete: auth.handleSocialSignUpComplete,
          onGoogleSignIn: auth.handleGoogleSignIn,
          onAppleSignIn: auth.handleAppleSignIn,
          onBack: auth.handleBack,
          onLoginClick: () => auth.handleNavigate("login"),
          logoUrl,
          heroImage,
          t: auth.t,
          socialAuthData,
          initialEmail: urlEmail
        }
      );
    case "forgot-password":
      return /* @__PURE__ */ jsx10(
        ForgotPasswordPage,
        {
          onSendResetEmail: auth.handleSendResetEmail,
          onBack: auth.handleBack,
          onLoginClick: () => auth.handleNavigate("login"),
          logoUrl,
          t: auth.t
        }
      );
    case "otp-verification":
      return /* @__PURE__ */ jsx10(
        OTPVerificationPage,
        {
          onVerifyOTP: auth.handleVerifyOTP,
          onResendOTP: auth.handleResendOTP,
          email,
          logoUrl,
          onBack: auth.handleBack,
          onLoginClick: () => auth.handleNavigate("login"),
          t: auth.t
        }
      );
    case "validate-forgot-password":
      return /* @__PURE__ */ jsx10(
        ResetPasswordPage,
        {
          onResetPassword: auth.handleResetPassword,
          logoUrl,
          onBack: auth.handleBack,
          email,
          code,
          onLoginClick: () => auth.handleNavigate("login"),
          t: auth.t
        }
      );
    case "account-linking":
      if (!accountLinkingData) {
        return null;
      }
      return /* @__PURE__ */ jsx10(
        AccountLinkingPage,
        {
          accountLinkingData,
          onConfirmLinking: auth.handleAccountLinking,
          onLoginWithEmail: () => {
            auth.setEmail(accountLinkingData.email);
            auth.handleNavigate("login", { email: accountLinkingData.email });
          },
          onBack: auth.handleBack,
          logoUrl,
          heroImage,
          t: auth.t
        }
      );
    case "login":
    default:
      return /* @__PURE__ */ jsx10(
        LoginPage,
        {
          onLogin: auth.handleLogin,
          onEmailSubmit: auth.handleEmailSubmit,
          onGoogleSignIn: auth.handleGoogleSignIn,
          onAppleSignIn: auth.handleAppleSignIn,
          onBack: auth.handleBack,
          onSignUpClick: () => auth.handleNavigate("signup"),
          onForgotPasswordClick: () => auth.handleNavigate("forgot-password"),
          logoUrl,
          heroImage,
          showHeroText,
          t: auth.t,
          emailSubmitted: auth.emailSubmitted,
          authMethod: auth.authMethod,
          isLoading: auth.isLoading,
          showSignUpLink,
          initialError
        }
      );
  }
}

// src/router/routes.ts
var AUTH_ROUTES = {
  LOGIN: "/auth/login",
  SIGNUP: "/auth/signup",
  FORGOT_PASSWORD: "/auth/forgot-password",
  RESET_PASSWORD: "/auth/validate-forgot-password",
  LOGOUT: "/auth/logout"
};
var ROUTE_CONFIGS = {
  [AUTH_ROUTES.LOGIN]: {
    path: AUTH_ROUTES.LOGIN,
    requiresAuth: false,
    redirectIfAuthenticated: true
  },
  [AUTH_ROUTES.SIGNUP]: {
    path: AUTH_ROUTES.SIGNUP,
    requiresAuth: false,
    redirectIfAuthenticated: true
  },
  [AUTH_ROUTES.FORGOT_PASSWORD]: {
    path: AUTH_ROUTES.FORGOT_PASSWORD,
    requiresAuth: false,
    redirectIfAuthenticated: false
  },
  [AUTH_ROUTES.RESET_PASSWORD]: {
    path: AUTH_ROUTES.RESET_PASSWORD,
    requiresAuth: false,
    redirectIfAuthenticated: false
  },
  [AUTH_ROUTES.LOGOUT]: {
    path: AUTH_ROUTES.LOGOUT,
    requiresAuth: true,
    redirectIfAuthenticated: false
  }
};

// src/router/RouterProvider.tsx
import { createContext as createContext3, useContext as useContext3, useMemo as useMemo3 } from "react";
import { jsx as jsx11 } from "react/jsx-runtime";
var AuthRouterContext = createContext3(null);
function AuthRouterProvider({
  children,
  routerAdapter,
  basePath = ""
}) {
  const getFullPath = (route) => {
    return `${basePath}${route}`;
  };
  const navigate = (route) => {
    routerAdapter.push(getFullPath(route));
  };
  const navigateWithParams = (route, params) => {
    const queryString = new URLSearchParams(params).toString();
    const path = queryString ? `${getFullPath(route)}?${queryString}` : getFullPath(route);
    routerAdapter.push(path);
  };
  const replace = (route) => {
    routerAdapter.replace(getFullPath(route));
  };
  const goBack = () => {
    routerAdapter.back();
  };
  const getCurrentPath = () => {
    return routerAdapter.getCurrentPath();
  };
  const value = useMemo3(
    () => ({
      navigate,
      navigateWithParams,
      replace,
      goBack,
      getCurrentPath,
      routes: AUTH_ROUTES
    }),
    [routerAdapter, basePath]
  );
  return /* @__PURE__ */ jsx11(AuthRouterContext.Provider, { value, children });
}
function useAuthRouter() {
  const context = useContext3(AuthRouterContext);
  if (!context) {
    throw new Error("useAuthRouter must be used within an AuthRouterProvider");
  }
  return context;
}

// src/router/resolveOAuthCallback.ts
var STATUS_ACCOUNT_EXISTS = "account_exists";
var STATUS_PENDING_REGISTRATION = "pending_registration";
function resolveOAuthCallback(params) {
  const { status, accessToken, email, provider, firstName, lastName } = params;
  const fallbackRoute = params.urlRoute || "login";
  const hasSocialPayload = Boolean(accessToken && email && provider);
  if (!hasSocialPayload) {
    return { effectiveRoute: fallbackRoute };
  }
  const payload = {
    accessToken,
    email,
    provider,
    firstName: firstName || void 0,
    lastName: lastName || void 0
  };
  if (status === STATUS_ACCOUNT_EXISTS) {
    return {
      effectiveRoute: "account-linking",
      accountLinkingData: payload
    };
  }
  if (status === STATUS_PENDING_REGISTRATION) {
    return {
      effectiveRoute: "signup",
      socialAuthData: payload
    };
  }
  return { effectiveRoute: fallbackRoute };
}

// src/router/resolveOAuthTokenCallback.ts
var CLAIM_NAME_ID = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier";
var CLAIM_NAME = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";
var CLAIM_EMAIL = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";
function decodeJwt(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = (parts[1] ?? "").replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}
function resolveOAuthTokenCallback(params) {
  const { token, expiresAt } = params;
  const redirectTo = params.redirectTo || "/";
  if (!token || !expiresAt) {
    return { ok: false, errorCode: "missing_token" };
  }
  const decoded = decodeJwt(token);
  if (!decoded) {
    return { ok: false, errorCode: "invalid_token" };
  }
  const nameParts = (decoded[CLAIM_NAME] || "").split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";
  return {
    ok: true,
    redirectTo,
    session: {
      token,
      tokenExpiration: Date.parse(expiresAt),
      user: {
        email: decoded[CLAIM_EMAIL] || "",
        firstName,
        lastName,
        externalId: decoded[CLAIM_NAME_ID] || "",
        phoneCountryCode: "",
        phone: "",
        avatarUrl: "",
        authProviderType: "GOOGLE" /* GOOGLE */,
        roles: []
      }
    }
  };
}

// src/localization/index.ts
var locales = {
  en: en_default,
  es: es_default
};

// src/components/index.ts
import {
  SimpleAuthIcon,
  SimpleGoogleIcon,
  SimpleFacebookIcon,
  SimpleAppleIcon,
  AuthProviderButton as AuthProviderButton2,
  PasswordStrengthMeter as PasswordStrengthMeter3,
  Progress,
  cn as cn4,
  Input as Input6,
  Label as Label6,
  Checkbox as Checkbox2,
  Select as Select2,
  SelectContent as SelectContent2,
  SelectItem as SelectItem2,
  SelectTrigger as SelectTrigger2,
  SelectValue as SelectValue2
} from "ui";
import { SELECT_STATES as SELECT_STATES2 } from "ui/src/components/Select";
import { COUNTRIES_PHONE_CODES as COUNTRIES_PHONE_CODES2 } from "ui/src/enums/CountryCodes";
export {
  AUTH_ROUTES,
  AccountLinkingPage,
  AuthContext,
  AuthProvider,
  AuthProviderButton2 as AuthProviderButton,
  AuthRouter,
  AuthRouterContext,
  AuthRouterProvider,
  AuthenticationCoreProvider,
  CHECK_EMAIL_EXISTS_QUERY,
  COUNTRIES_PHONE_CODES2 as COUNTRIES_PHONE_CODES,
  Checkbox2 as Checkbox,
  FORGOT_PASSWORD_MUTATION,
  ForgotPasswordPage,
  GET_LOGIN_METHOD_QUERY,
  GET_ME_QUERY,
  Input6 as Input,
  LINK_ACCOUNT_MUTATION,
  LOGIN_MUTATION,
  LOGOUT_MUTATION,
  Label6 as Label,
  LocalizationContext,
  LocalizationProvider,
  LoginPage,
  OTPVerificationPage,
  PasswordStrengthMeter3 as PasswordStrengthMeter,
  Progress,
  REFRESH_TOKEN_MUTATION,
  RESET_PASSWORD_MUTATION,
  ROUTE_CONFIGS,
  ResetPasswordPage,
  SELECT_STATES2 as SELECT_STATES,
  SIGNUP_INPUT_CLASS_NAME,
  SIGNUP_MUTATION,
  Select2 as Select,
  SelectContent2 as SelectContent,
  SelectItem2 as SelectItem,
  SelectTrigger2 as SelectTrigger,
  SelectValue2 as SelectValue,
  SignupPage as SignUpPage,
  SignupPage,
  SimpleAppleIcon,
  SimpleAuthIcon,
  SimpleFacebookIcon,
  SimpleGoogleIcon,
  VERIFY_TOKEN_QUERY,
  cn4 as cn,
  createAuthenticationClient,
  en_default as en,
  es_default as es,
  getAuthenticationClient,
  locales,
  resetAuthenticationClient,
  resolveOAuthCallback,
  resolveOAuthTokenCallback,
  useAuth,
  useAuthHandlers,
  useAuthRouter,
  useForgotPassword,
  useLinkAccount,
  useLocalization,
  useLogin,
  useLoginMethod,
  useLogout,
  usePageRestore,
  useResetPassword,
  useSignup
};
//# sourceMappingURL=index.js.map